import {
  BALL_MIN_RUN,
  BALL_RETRY_TICKS,
  BALL_SPEEDUP,
  BALL_WARN_TICKS,
  type BallState,
} from './balls';
import { BOOST_IDS, BOOST_TICKS, MAGNET_REACH, TURBO_SPEED, type BoostId } from './boosts';
import { isItemId, ITEM_TRIMMER, type ItemId } from './items';
import { POOF_TICKS, SIM_RULES, type SimRules } from './simRules';
import { Critters, type CritterWorld } from './critters';
import {
  applyTurn,
  Dir,
  DIRECTIONS,
  dx,
  dy,
  opposite,
  turnBetween,
  turnLeft,
  turnRight,
  type Direction,
  type Turn,
} from './direction';
import { EventQueue, type SimEvent } from './events';
import { chooseSide, footprint, pivotTiles, wingTiles, type CutWidth, type Pos } from './footprint';
import {
  DOWNHILL,
  FLAT_SLOWDOWN,
  Grid,
  HEAVY_BREAK_SLOWDOWN,
  HEAVY_BREAK_TIER,
  UPHILL,
  UPHILL_EXTRA_FUEL,
  TALL_GRASS_EXTRA_FUEL,
  TALL_GRASS_SLOWDOWN,
  Tile,
  isSolid,
  isTallGrass,
  needsMowing,
  trimmerReachesOver,
  type MowResult,
} from './grid';
import {
  fallTiles,
  moundSlope,
  TREASURE_VALUE,
  validateSimLevel,
  type HerdSpec,
  type SimLevel,
} from './level';
import {
  createMowerState,
  validateMowerSpec,
  type Intent,
  type MowerSpec,
  type MowerState,
  type SteerIntent,
} from './mower';
import {
  FALL_WARN_TICKS,
  MOLE_RETRY_TICKS,
  MOLE_WARN_TICKS,
  type FallState,
  type MoleState,
} from './moles';
import { rivalHeading, type RivalState } from './rival';
import { Rng } from './rng';
import { initialToolStates, validateToolSpec, type ToolSpec, type ToolState } from './tools';
import {
  CRASH_INVULN_TICKS,
  INTENT_BUFFER_CENTERS,
  SPRINKLER,
  TURN_GRACE_MAX_TICKS,
  BRINK_GRACE,
} from './tuning';
import { canTurn, recordBounce, recordTileTraveled, recordTurn, tickTurnRule } from './turnRules';

/** Mower soccer: a kick rolls the ball this many tiles (a squirt out sideways, fewer). */
export const KICK_TILES = 3;
const SQUIRT_TILES = 1;
/** Ticks the ball takes to roll a tile (quicker than any mower). */
export const BALL_TICKS = 5;

export type SimStatus = 'running' | 'won' | 'broken' | 'outOfFuel' | 'caught' | 'outmowed' | 'late';

/** Where and whether the buffered steering request will apply at the next tile center. */
export interface TurnPreview {
  /** The tile center where the decision will be made. */
  x: number;
  y: number;
  turn: Turn;
  /** Heading after the turn. */
  dir: Direction;
  /** `ok`: it will turn there. `blocked`: target tile is an obstacle. `rule`: not allowed yet. */
  outcome: 'ok' | 'blocked' | 'rule' | 'wet';
}

/** State carried in from a previous lawn (endless runs). */
export interface CarryOver {
  hp?: number;
  fuel?: number;
}

export interface LoggedIntent {
  /** The tick during which the intent was applied. */
  tick: number;
  intent: Intent;
}

/**
 * Head to head (docs/HEAD_TO_HEAD.md): one mower on a match's lawn, with what is its own: its
 * state, steering requests and their log, whether it is still going, and the grass and coins it
 * has taken.
 */
interface Driver {
  m: MowerState;
  inbox: Intent[];
  log: LoggedIntent[];
  status: SimStatus;
  tiles: number;
  coins: number;
  /** Ticks left spinning in place, dizzy from a bump in the side (0: not dizzy). */
  dizzy: number;
  /** Ticks left after waking in which another bump in the side cannot make it dizzy again. */
  steady: number;
}

/**
 * Head to head: how long a mower bumped in the side (driven into while crossing the other's path)
 * spins in place, dizzy: two seconds. No heart lost; it cannot move, mow or steer meanwhile.
 */
export const DIZZY_TICKS = 120;
/** Head to head: after waking, three seconds in which it cannot be made dizzy again. */
export const STEADY_TICKS = 180;

/** Head to head: how a match ended. `winner` is a seat, or null for a draw. */
export interface MatchResult {
  winner: number | null;
  /**
   * More than half the grass, the whole lawn mowed, or a mower breaking down; in a soccer match,
   * the goals that win, or the whistle; in a round-up, the sheep that win, or the whistle; in a
   * maze, the first into the middle (`maze`), or the whistle. A party of three or four
   * (docs/PARTY.md) mowing: the leader can no longer be caught (`lead`).
   */
  why: 'half' | 'done' | 'brokeDown' | 'goals' | 'time' | 'sheep' | 'maze' | 'lead';
  /** Grass tiles and coins each seat took, and how each seat's mower ended up. */
  tiles: number[];
  coins: number[];
  ends: SimStatus[];
  /** A soccer match: each seat's goals. */
  goals?: number[];
  /** A round-up match: the sheep in each seat's pen. */
  penned?: number[];
  /** A maze match: each seat's tiles from the plaza along the paths, as it ended. */
  distance?: number[];
}

/** Where a mower stood as a tick began (head to head's bumps are decided against it). */
interface Standing {
  x: number;
  y: number;
  dir: Direction;
  progress: number;
  /** Ticks it takes to cross the tile it is on. */
  cross: number;
}

/**
 * Whether a mower standing like `s` holds (x, y): the tile it is on (until it is more than halfway
 * out of it) and the tile it is driving onto. The Rival Robo-Mower's rule, for every mower.
 */
function holds(s: Standing, x: number, y: number): boolean {
  if (s.progress === 0) return s.x === x && s.y === y;
  if (s.x + dx(s.dir) === x && s.y + dy(s.dir) === y) return true;
  return s.x === x && s.y === y && s.progress * 2 < s.cross;
}

/** Lawn features not yet fair to several mowers: a match cannot have them (docs/HEAD_TO_HEAD.md). */
const NOT_IN_MATCHES = ['moles', 'balls', 'falls', 'rival', 'herd', 'art', 'storm'] as const;

/**
 * Deterministic game state for one level attempt. Advance with {@link step}, one fixed tick at a
 * time; read state and the returned events to render. Same level + mower + intents on the same
 * ticks always produce the same result.
 *
 * Movement model: the mower always moves forward, one tile every `ticksPerTile` ticks. Steering is
 * decided only at tile centers. A buffered intent applies at the first center where the mower's
 * turn rule allows it and the target tile is open; it expires after a couple of centers.
 */
/** The string trimmer cuts any grass right down in one go. */
const TRIM_BITE = 3;

export class Simulation {
  readonly level: SimLevel;
  readonly mowerSpec: MowerSpec;
  readonly grid: Grid;
  readonly rng: Rng;
  readonly critters: Critters;
  /** Boss rival: the neighbor's robot mower (null on other lawns). */
  private readonly rivalState: RivalState | null;
  /** Moles: hills raised so far, ticks to the next, and a tremble before one comes up. */
  private readonly moleState: MoleState | null;
  private readonly moleRng: Rng;
  /** Falling trees: one state per tree in the level's `falls`. */
  private readonly fallStates: FallState[];
  /** Hills: the downhill direction of each tile (row-major), or -1 where the lawn is level. */
  private readonly slopes: Int8Array;
  /** Stray balls: the one in play (or null), ticks to the next whistle, and their own dice. */
  private ballState: BallState | null = null;
  private ballTimer = 0;
  private readonly ballRng: Rng;
  /** Boosts: ticks each has left to run, and which have been used this attempt. */
  private readonly boostLeft: Record<BoostId, number> = {
    shield: 0,
    turbo: 0,
    magnet: 0,
    treat: 0,
  };
  private readonly boostsUsed = new Set<BoostId>();
  /** Item boxes (src/sim/items.ts): whether this lawn has any, what is held, the trimmer's wait. */
  readonly hasItems: boolean;
  private readonly held: Record<ItemId, number> = { turbo: 0, trim: 0 };
  private trimTicks = 0;
  /** The mower in play: seat 0's, or (while a match steps a seat) that seat's. */
  private m: MowerState;
  private readonly critterWorld: CritterWorld;
  private readonly events = new EventQueue();
  private inbox: Intent[] = [];
  private log: LoggedIntent[] = [];
  /**
   * Every mower on the lawn, seat 0 first. A lawn without seats has only seat 0 and plays exactly
   * as it always has; with seats it is a head-to-head match (docs/HEAD_TO_HEAD.md).
   */
  private readonly drivers: Driver[] = [];
  /** The seat whose mower, requests and status are in `m`, `inbox`, `log` and `currentStatus`. */
  private seatIndex = 0;
  /** A match's end, once over. */
  private matchEnd: { winner: number | null; why: MatchResult['why'] } | null = null;
  /** Head to head: which seat mowed each tile (-1: nobody yet), for showing who owns what. */
  private owners: Int8Array | null = null;
  /** Where each mower stood as this tick began, and the tiles mowers set off for during it. */
  private standing: Standing[] = [];
  private readonly claims = new Map<number, number>();
  /** Head to head: the seats bumped in the side this tick, dizzy from the next. */
  private readonly dazed: number[] = [];
  private currentTick = 0;
  private currentStatus: SimStatus = 'running';
  private trampledCount = 0;
  private smashedCount = 0;
  private hiveCount = 0;
  /** Regrowing meadow: which patch each tile belongs to (-1: none), and each patch's state. */
  private readonly patchOf: Int16Array;
  private readonly patches: { tiles: readonly number[]; lastMow: number; done: boolean }[] = [];
  /** Lawn items smashed, by break tier (0..4). */
  private readonly smashedByTier = [0, 0, 0, 0, 0];
  private chewedCount = 0;
  private dogBumpCount = 0;
  private sprinklerCount = 0;
  private readonly collected: Uint8Array;
  private coinCount = 0;
  private spareCount = 0;
  private treasureCount = 0;
  /** Mower soccer: the ball (rolling `left` more tiles), and the goals scored. */
  private soccerBall: {
    x: number;
    y: number;
    dir: Direction;
    left: number;
    progress: number;
  } | null = null;
  private goalCount = 0;
  /** A soccer match: each seat's goals (docs/SOCCER_MATCH.md). */
  private seatGoals: number[] = [];
  readonly tools: readonly ToolSpec[];
  /** Health and fuel carried in from the previous lawn (an Endless run), as given. */
  readonly carry: Readonly<CarryOver>;
  private readonly toolStates: ToolState[];

  /** The rules that changed after a version shipped (src/sim/simRules.ts). */
  readonly rules: Readonly<SimRules>;

  constructor(
    level: SimLevel,
    mowerSpec: MowerSpec,
    carry: CarryOver = {},
    tools: readonly ToolSpec[] = [],
    rules: Readonly<SimRules> = SIM_RULES,
  ) {
    this.rules = rules;
    validateSimLevel(level);
    validateMowerSpec(mowerSpec);
    tools.forEach(validateToolSpec);
    this.tools = tools;
    this.carry = { ...carry };
    this.toolStates = initialToolStates(tools);
    this.level = level;
    this.mowerSpec = mowerSpec;
    this.grid = new Grid(level.width, level.height, level.tiles, level.toughness, level.extras);
    this.patchOf = new Int16Array(level.width * level.height).fill(-1);
    for (const tiles of level.regrow?.patches ?? []) {
      for (const i of tiles) this.patchOf[i] = this.patches.length;
      this.patches.push({ tiles, lastMow: -1, done: false });
    }
    this.rng = Rng.fromSeed(`${level.seed}:sim`);
    const side = Simulation.spawnSide(level, mowerSpec, this.grid);
    if (side === null) {
      throw new RangeError(`${level.id}: ${mowerSpec.name} doesn't fit at the start`);
    }
    this.m = createMowerState(mowerSpec, level.start, side);
    if (carry.hp !== undefined) this.m.hp = Math.max(1, Math.min(mowerSpec.maxHp, carry.hp));
    if (carry.fuel !== undefined && mowerSpec.fuelTiles !== undefined) {
      this.m.fuel = Math.max(1, Math.min(mowerSpec.fuelTiles, carry.fuel));
    }
    this.critters = new Critters(level.critters ?? [], this.rng);
    this.rivalState = level.rival ? { ...level.rival.start, progress: 0, tiles: 0 } : null;
    // The moles draw from their own stream, so they never change how critters behave.
    this.moleRng = Rng.fromSeed(`${level.seed}:moles`);
    this.moleState = level.moles
      ? { raised: 0, timer: level.moles.first * mowerSpec.ticksPerTile, warn: null }
      : null;
    this.ballRng = Rng.fromSeed(`${level.seed}:balls`);
    if (level.balls) this.ballTimer = level.balls.first * mowerSpec.ticksPerTile;
    if (level.soccer) {
      const { x, y } = level.soccer.center;
      this.soccerBall = { x, y, dir: 0 as Direction, left: 0, progress: 0 };
    }
    this.slopes = new Int8Array(level.width * level.height).fill(-1);
    for (const hill of level.hills ?? []) {
      for (let y = hill.y; y < hill.y + hill.h; y++) {
        for (let x = hill.x; x < hill.x + hill.w; x++) {
          const d = moundSlope(hill, x, y);
          if (d !== null) this.slopes[y * level.width + x] = d;
        }
      }
    }
    this.fallStates = (level.falls ?? []).map((f) => ({
      timer: f.at * mowerSpec.ticksPerTile,
      warn: 0,
      down: false,
    }));
    this.critterWorld = this.makeCritterWorld();
    this.collected = new Uint8Array(level.pickups?.length ?? 0);
    this.hasItems = (level.pickups ?? []).some((p) => isItemId(p.kind));
    this.drivers.push({
      m: this.m,
      inbox: this.inbox,
      log: this.log,
      status: 'running',
      tiles: 0,
      coins: 0,
      dizzy: 0,
      steady: 0,
    });
    this.arriveAt(this.m.x, this.m.y);
    if (level.seats) this.seatMatch(level.seats);
  }

  /**
   * Head to head: the other seats' mowers, each mowing its start tile. Only features fair to
   * every mower, a one-tile mower, and no tools (docs/HEAD_TO_HEAD.md).
   */
  private seatMatch(seats: NonNullable<SimLevel['seats']>): void {
    const level = this.level;
    const spec = this.mowerSpec;
    // What seat 0's mower has done so far (its start tile) is its own.
    this.events.stampSeat(0, 0);
    const unfair: string[] = NOT_IN_MATCHES.filter((k) => level[k] !== undefined);
    // A round-up's sheep are the only critters a match may have.
    const critters = (level.critters ?? []).filter((c) => !level.roundup || c.kind !== 'sheep');
    if (critters.length > 0) unfair.push('critters');
    if (unfair.length > 0) {
      throw new RangeError(`${level.id}: a match cannot have ${unfair.join(', ')} yet`);
    }
    if ((spec.cutWidth ?? 1) !== 1 || spec.wings || this.tools.length > 0) {
      throw new RangeError(`${level.id}: a match needs a one-tile mower and no tools`);
    }
    // Seat 0's start tile is its own already.
    this.owners = new Int8Array(level.width * level.height).fill(-1);
    this.owners[level.start.y * level.width + level.start.x] = 0;
    seats.slice(1).forEach((s, k) => {
      this.drivers.push({
        m: createMowerState(spec, s),
        inbox: [],
        log: [],
        status: 'running',
        tiles: 0,
        coins: 0,
        dizzy: 0,
        steady: 0,
      });
      this.inSeat(k + 1, () => this.arriveAt(s.x, s.y));
    });
  }

  /**
   * Runs `fn` as seat `seat`: its mower, requests and status in play, and the events it makes
   * marked as its own; then back to the seat before.
   */
  private inSeat(seat: number, fn: () => void): void {
    const d = this.drivers[seat]!;
    const before = this.seatIndex;
    const beforeStatus = this.currentStatus;
    const mark = this.events.size;
    this.seatIndex = seat;
    this.m = d.m;
    this.inbox = d.inbox;
    this.log = d.log;
    this.currentStatus = d.status;
    try {
      fn();
    } finally {
      d.status = this.currentStatus;
      this.events.stampSeat(mark, seat);
      const back = this.drivers[before]!;
      this.seatIndex = before;
      this.m = back.m;
      this.inbox = back.inbox;
      this.log = back.log;
      this.currentStatus = beforeStatus;
    }
  }

  /** Which side a wide mower's second track starts on, or null if the mower can't fit at all. */
  private static spawnSide(level: SimLevel, spec: MowerSpec, grid: Grid): 'left' | 'right' | null {
    if ((spec.cutWidth ?? 1) === 1) return 'left';
    return chooseSide(
      (x, y) => !grid.isBlockedFor(x, y, spec.cutPower, spec.breakPower ?? 0),
      level.start,
    );
  }

  /** Whether a mower can start this level at all (wide mowers need room beside the start). */
  static fits(level: SimLevel, spec: MowerSpec): boolean {
    return (
      Simulation.spawnSide(
        level,
        spec,
        new Grid(level.width, level.height, level.tiles, level.toughness),
      ) !== null
    );
  }

  get tick(): number {
    return this.currentTick;
  }

  /** Charges and cooldown of each tool slot. */
  toolState(slot: number): Readonly<ToolState> | undefined {
    return this.toolStates[slot];
  }

  get cutWidth(): CutWidth {
    return this.mowerSpec.cutWidth ?? 1;
  }

  /** Tiles the mower covers when anchored at (x, y) heading `dir` (its own side). */
  footprintAt(x: number, y: number, dir: Direction): Pos[] {
    return footprint(x, y, dir, this.cutWidth, this.m.side);
  }

  /** Whether the mower could stand anchored at (x, y) heading `dir`. */
  canOccupy(x: number, y: number, dir: Direction): boolean {
    return this.footprintAt(x, y, dir).every((p) => !this.blocks(p.x, p.y));
  }

  /** Whether turning at (x, y) from `from` to `to` has room and leads somewhere open. */
  canTurnInto(x: number, y: number, from: Direction, to: Direction): boolean {
    if (to === opposite(from)) {
      // A reversal keeps the same physical footprint (the side flips), so no sweep.
      const side = this.cutWidth > 1 && this.m.side === 'left' ? 'right' : this.m.side;
      return footprint(x + dx(to), y + dy(to), to, this.cutWidth, side).every(
        (p) => !this.blocks(p.x, p.y),
      );
    }
    const sweep = pivotTiles(x, y, from, to, this.cutWidth, this.m.side);
    if (sweep.some((p) => this.blocksTurn(p.x, p.y))) return false;
    return this.footprintAt(x + dx(to), y + dy(to), to).every(
      (p) => !this.blocksTurn(p.x, p.y, this.rules.turnIntoBall),
    );
  }

  /**
   * Whether (x, y) stops a turn. Rules 6: a squirrel or rabbit there does not (it poofs once the
   * mower drives on), nor, herding, a sheep (nudged on as one straight ahead is). Rules 9, with
   * `ballOk` (the tiles the turn leads into): nor the ball, kicked on as the mower drives off.
   */
  private blocksTurn(x: number, y: number, ballOk = false): boolean {
    const i = this.critters.indexAt(x, y);
    const kind = i >= 0 ? this.critters.list[i]?.kind : undefined;
    // A squirrel or rabbit there poofs once the mower drives on (rules 6), so it does not either.
    const small = this.rules.softSmallCritters && (kind === 'squirrel' || kind === 'rabbit');
    const herding = !!this.level.herd || !!this.level.roundup;
    const sheep = this.rules.turnIntoSheep && herding && kind === 'sheep';
    if (small || sheep) {
      return (
        this.grid.isBlockedFor(x, y, this.mowerSpec.cutPower, this.mowerSpec.breakPower ?? 0) ||
        this.rivalOccupies(x, y) ||
        this.ballAt(x, y)
      );
    }
    if (ballOk && this.ballAt(x, y)) {
      return (
        this.grid.isBlockedFor(x, y, this.mowerSpec.cutPower, this.mowerSpec.breakPower ?? 0) ||
        this.critters.occupied(x, y) ||
        this.rivalOccupies(x, y)
      );
    }
    return this.blocks(x, y);
  }

  get status(): SimStatus {
    return this.currentStatus;
  }

  get mower(): Readonly<MowerState> {
    return this.m;
  }

  /** Flower-bed tiles driven over so far (each costs the "no trampling" bonus). */
  get trampled(): number {
    return this.trampledCount;
  }

  /** Lawn decorations (gnomes, bird baths) smashed so far. */
  get smashed(): number {
    return this.smashedCount;
  }

  /** Beehives smashed so far (each costs coins and a sting). */
  get hives(): number {
    return this.hiveCount;
  }

  /** Lawn items smashed so far, counted by break tier (index 0..4), for the damage bill. */
  get smashedTiers(): readonly number[] {
    return this.smashedByTier;
  }

  /**
   * Boss storm: the first row the rain has reached (rows from here down are under it), or null on
   * lawns without a storm. Starts below the lawn.
   */
  stormRow(tick = this.currentTick): number | null {
    const storm = this.level.storm;
    if (!storm) return null;
    const perTile = this.mowerSpec.ticksPerTile;
    const moving = tick - Math.round(storm.delayTiles * perTile);
    if (moving <= 0) return this.grid.height;
    if (!storm.accel) {
      return this.grid.height - Math.floor(moving / Math.max(1, Math.round(storm.pace * perTile)));
    }
    // Rain Chase: each row comes a little faster than the last.
    let rows = 0;
    let left = moving;
    while (rows < this.grid.height && left >= this.stormRowTicks(rows)) {
      left -= this.stormRowTicks(rows);
      rows++;
    }
    return this.grid.height - rows;
  }

  /** Ticks the rain takes to climb its `k`-th row (0-based). */
  private stormRowTicks(k: number): number {
    const storm = this.level.storm!;
    const pace = storm.accel
      ? Math.max(storm.minPace ?? 1, storm.pace - storm.accel * k)
      : storm.pace;
    return Math.max(1, Math.round(pace * this.mowerSpec.ticksPerTile));
  }

  /**
   * Boss storm: ticks until the rain reaches the mower's row if it stopped making progress (its
   * head start, counting the wait before the rain starts moving). Null on lawns without a storm.
   */
  stormEta(): number | null {
    const storm = this.level.storm;
    if (!storm) return null;
    const perTile = this.mowerSpec.ticksPerTile;
    const delay = Math.round(storm.delayTiles * perTile);
    let arrives = delay;
    for (let k = 0; k < this.grid.height - this.m.y; k++) arrives += this.stormRowTicks(k);
    return Math.max(0, arrives - this.currentTick);
  }

  /** Boss Hedge Maze: ticks until the garden party starts, or null on other lawns. */
  partyEta(): number | null {
    const party = this.level.party;
    if (!party) return null;
    return Math.max(0, party.tiles * this.mowerSpec.ticksPerTile - this.currentTick);
  }

  /**
   * Boss lawn art: how well the lawn matches the picture (0..1), the share of target tiles mowed
   * with the right stripe. Null on other lawns.
   */
  artMatch(): number | null {
    const art = this.level.art;
    if (!art) return null;
    let targets = 0;
    let matched = 0;
    for (let y = 0; y < this.grid.height; y++) {
      for (let x = 0; x < this.grid.width; x++) {
        const want = art.pattern[y * this.grid.width + x] ?? 0;
        if (want === 0) continue;
        targets++;
        if (this.artAxisAt(x, y) === want) matched++;
      }
    }
    return targets === 0 ? 1 : matched / targets;
  }

  /** The stripe a tile currently has: across (mowed east/west), along (north/south), or none. */
  artAxisAt(x: number, y: number): 0 | 1 | 2 {
    if (this.grid.tileAt(x, y) !== Tile.Mowed) return 0;
    const d = this.grid.stripeAt(x, y);
    return d === Dir.E || d === Dir.W ? 1 : 2;
  }

  /** Sprinkler heads broken so far. */
  get sprinklersBroken(): number {
    return this.sprinklerCount;
  }

  /** Where a sprinkler head's cycle starts (from its position, so heads don't pulse in unison). */
  private static sprinklerOffset(x: number, y: number): number {
    return (x * 73 + y * 151) % SPRINKLER.period;
  }

  /** A sprinkler head's state at `tick`: spraying, about to spray, or off (or broken: off). */
  sprinklerState(x: number, y: number, tick = this.currentTick): 'on' | 'warn' | 'off' {
    if (this.grid.tileAt(x, y) !== Tile.Sprinkler) return 'off';
    const t = (tick + Simulation.sprinklerOffset(x, y)) % SPRINKLER.period;
    if (t < SPRINKLER.on) return 'on';
    return t >= SPRINKLER.period - SPRINKLER.warn ? 'warn' : 'off';
  }

  /** Whether (x, y) is being sprayed at `tick`: wet tiles are slippery (no turning on them). */
  isWet(x: number, y: number, tick = this.currentTick): boolean {
    if (!this.grid.inSprinklerReach(x, y)) return false;
    for (let oy = -1; oy <= 1; oy++) {
      for (let ox = -1; ox <= 1; ox++) {
        if (ox === 0 && oy === 0) continue;
        if (this.sprinklerState(x + ox, y + oy, tick) === 'on') return true;
      }
    }
    return false;
  }

  /** The mower stands (or will stand at `tick`) on a wet tile at (x, y): it slides, no turning. */
  private slippery(x = this.m.x, y = this.m.y, tick = this.currentTick): boolean {
    return this.footprintAt(x, y, this.m.dir).some((p) => this.isWet(p.x, p.y, tick));
  }

  /** Times the mower bumped into the dog (a hit, and a coin penalty). */
  get dogBumps(): number {
    return this.dogBumpCount;
  }

  /** Garden hose tiles chewed so far. */
  get chewed(): number {
    return this.chewedCount;
  }

  /** Coins picked up so far. */
  get coins(): number {
    return this.coinCount;
  }

  /**
   * Coins paid for spare pickups (gas without a tank, repairs at full health). Paid out like coins
   * but not counted toward collecting every coin on the lawn.
   */
  /** Coins from a secret yard's treasure (paid on a clear; not part of the all-coins star). */
  get treasure(): number {
    return this.treasureCount;
  }

  get spareCoins(): number {
    return this.spareCount;
  }

  /** Coins in the level. */
  get totalCoins(): number {
    return (this.level.pickups ?? []).filter((p) => p.kind === 'coin').length;
  }

  /** Whether the pickup at `index` (in the level's list) has been collected. */
  isCollected(index: number): boolean {
    return this.collected[index] === 1;
  }

  /** True once a rare item has been picked up (kept only if the lawn is finished). */
  get rareCollected(): boolean {
    return (this.level.pickups ?? []).some((p, i) => p.kind === 'rare' && this.collected[i] === 1);
  }

  /** True if (x, y) would stop this mower (obstacle, fence, brush it can't cut, or a critter). */
  blocks(x: number, y: number): boolean {
    return (
      this.grid.isBlockedFor(x, y, this.mowerSpec.cutPower, this.mowerSpec.breakPower ?? 0) ||
      this.critters.occupied(x, y) ||
      this.rivalOccupies(x, y) ||
      this.ballAt(x, y)
    );
  }

  /** Mower soccer: the ball on the pitch, or null on other lawns. */
  get pitchBall(): Readonly<{
    x: number;
    y: number;
    dir: Direction;
    left: number;
    progress: number;
  }> | null {
    return this.soccerBall;
  }

  /** Mower soccer: goals scored so far. */
  get goals(): number {
    return this.goalCount;
  }

  /** Mower soccer: ticks until the halftime whistle, or null on other lawns. */
  halftimeEta(): number | null {
    const soccer = this.level.soccer;
    if (!soccer) return null;
    return Math.max(0, soccer.halfTiles * this.mowerSpec.ticksPerTile - this.currentTick);
  }

  private ballAt(x: number, y: number): boolean {
    const b = this.soccerBall;
    return b !== null && b.x === x && b.y === y;
  }

  /** The tile center where the mower makes its next steering decision. */
  nextDecisionTile(): { x: number; y: number } {
    const m = this.m;
    return m.progress === 0 || m.brink || m.backing
      ? { x: m.x, y: m.y }
      : { x: m.x + dx(m.dir), y: m.y + dy(m.dir) };
  }

  /** Ticks the mower noses toward a blocked tile before it crashes (`BRINK_GRACE`). */
  private brinkTicks(): number {
    return Math.floor((this.mowerSpec.ticksPerTile * BRINK_GRACE.num) / BRINK_GRACE.den);
  }

  /**
   * Ticks the mower needs to cross the tile it is currently on: slower in the slow gear, on mud,
   * uphill (quicker downhill), when pushing into tall grass, and on a flat tire.
   */
  crossTicks(): number {
    const spec = this.mowerSpec;
    const m = this.m;
    const base = m.slow && spec.throttle ? spec.throttle.slowTicksPerTile : spec.ticksPerTile;
    const level = this.grid.ticksToCross(m.x, m.y, base);
    const climb = this.climbAt(m.x, m.y, m.dir);
    const ticks =
      climb === 'up'
        ? Math.ceil((level * UPHILL.num) / UPHILL.den)
        : climb === 'down'
          ? Math.max(2, Math.ceil((level * DOWNHILL.num) / DOWNHILL.den))
          : level;
    const ahead = this.footprintAt(m.x + dx(m.dir), m.y + dy(m.dir), m.dir);
    // Rules 15: the Fairway's side reels pushing into tall grass are heavy going too.
    const reels =
      spec.wings && this.rules.wingsInTallGrass
        ? wingTiles(m.x + dx(m.dir), m.y + dy(m.dir), m.dir).filter((p) =>
            this.wingReaches(p.x, p.y),
          )
        : [];
    const heavy = [...ahead, ...reels].some((p) => isTallGrass(this.grid.tileAt(p.x, p.y)));
    const heavier = heavy
      ? Math.ceil((ticks * TALL_GRASS_SLOWDOWN.num) / TALL_GRASS_SLOWDOWN.den)
      : ticks;
    // Plowing through something big (a bench, a bush, a ball washer) is heavy going too.
    const plowing = ahead.some((p) => this.grid.toughnessAt(p.x, p.y) >= HEAVY_BREAK_TIER)
      ? Math.ceil((heavier * HEAVY_BREAK_SLOWDOWN.num) / HEAVY_BREAK_SLOWDOWN.den)
      : heavier;
    const slowed = m.flat ? Math.ceil((plowing * FLAT_SLOWDOWN.num) / FLAT_SLOWDOWN.den) : plowing;
    if (this.boostLeft.turbo === 0) return slowed;
    return Math.max(2, Math.ceil((slowed * TURBO_SPEED.num) / TURBO_SPEED.den));
  }

  /** The downhill direction at (x, y), or null where the lawn is level. */
  slopeAt(x: number, y: number): Direction | null {
    if (!this.grid.inBounds(x, y)) return null;
    const s = this.slopes[y * this.grid.width + x]!;
    return s < 0 ? null : (s as Direction);
  }

  /** Whether driving `dir` from (x, y) goes uphill, downhill, or along the level. */
  climbAt(x: number, y: number, dir: Direction): 'up' | 'down' | null {
    const s = this.slopeAt(x, y);
    if (s === null) return null;
    if (s === dir) return 'down';
    return s === opposite(dir) ? 'up' : null;
  }

  /** Every intent accepted so far, stamped with the tick it applied on (for replays). */
  get intentLog(): readonly LoggedIntent[] {
    return this.log;
  }

  /** Events produced before the first step (the start tile being mowed). */
  drainEvents(): SimEvent[] {
    return this.events.drain();
  }

  /**
   * Predicts the buffered intent's fate at the next tile center the mower decides at, without
   * changing any state. Null when nothing is pending (or the request is a no-op).
   */
  pendingTurnPreview(intent: SteerIntent | null = this.m.pendingIntent): TurnPreview | null {
    const m = this.m;
    if (!intent || this.currentStatus !== 'running') return null;
    const turn = this.resolveTurn(intent);
    if (turn === null) return null;
    // Nosing toward an obstacle (or backing up to park), the mower still decides at the tile it
    // is leaving.
    const atCenter = m.progress === 0 || m.brink || m.backing;
    const x = atCenter ? m.x : m.x + dx(m.dir);
    const y = atCenter ? m.y : m.y + dy(m.dir);
    // Rule state as it will be at that decision tick (see step(): decrement, then decide).
    const ticksToDecision = m.backing
      ? m.progress + 1
      : atCenter
        ? 1
        : this.crossTicks() - m.progress + 1;
    const future = {
      lastTurn: m.lastTurn,
      tilesSinceTurn: atCenter ? m.tilesSinceTurn : m.tilesSinceTurn + 1,
      pullbackCooldown: Math.max(0, m.pullbackCooldown - ticksToDecision),
    };
    const dir = applyTurn(m.dir, turn);
    let outcome: TurnPreview['outcome'] = 'ok';
    if (m.departed && !canTurn(this.mowerSpec.turnRule, future, turn)) outcome = 'rule';
    else if (!this.canTurnInto(x, y, m.dir, dir)) outcome = 'blocked';
    else if (this.slippery(x, y, this.currentTick + ticksToDecision)) outcome = 'wet';
    return { x, y, turn, dir, outcome };
  }

  /** Queues a steering request; it takes effect during the next {@link step}. */
  enqueue(intent: Intent): void {
    if (this.drivers.length > 1) {
      this.enqueueFor(0, intent);
      return;
    }
    // A finished attempt takes no input, except a revive for a mower that broke or ran dry.
    const revivable = this.currentStatus === 'broken' || this.currentStatus === 'outOfFuel';
    if (intent.type === 'revive' ? !revivable : this.currentStatus !== 'running') return;
    this.inbox.push(intent);
    this.log.push({ tick: this.currentTick + 1, intent });
  }

  /** Advances one tick and returns the events it produced. No-op once the attempt has ended. */
  step(): SimEvent[] {
    if (this.drivers.length > 1) return this.stepMatch();
    if (this.currentStatus !== 'running') {
      if (this.inbox.some((i) => i.type === 'revive')) this.revive();
      return this.events.drain();
    }
    this.currentTick++;

    tickTurnRule(this.m);
    if (this.m.invulnTicks > 0) this.m.invulnTicks--;
    this.tickBoosts();
    for (const t of this.toolStates) if (t.cooldown > 0) t.cooldown--;
    if (this.trimTicks > 0) this.trimTicks--;

    for (const intent of this.inbox) {
      if (intent.type === 'revive') continue; // only meaningful once the attempt has ended
      if (intent.type === 'throttle') {
        if (this.mowerSpec.throttle) this.m.slow = intent.slow;
      } else if (intent.type === 'tool') this.useTool(intent.slot);
      else if (intent.type === 'boost') this.useBoost(intent.boost);
      else if (intent.type === 'item') this.useItem(intent.item);
      else if (intent.type === 'park') this.requestPark();
      else this.receiveIntent(intent);
    }
    this.inbox.length = 0;

    this.move();
    if (this.currentStatus === 'running') this.updateStorm();
    if (this.currentStatus === 'running' && this.partyEta() === 0) {
      this.currentStatus = 'late';
      this.events.push({ type: 'late', tick: this.currentTick });
    }
    if (this.currentStatus === 'running') this.updateRival();
    if (this.currentStatus === 'running') this.updateSoccer();
    if (this.currentStatus === 'running') this.updateMoles();
    if (this.currentStatus === 'running') this.updateFalls();
    if (this.currentStatus === 'running') this.updateBall();
    if (this.currentStatus === 'running') this.updateRegrowth();
    if (this.currentStatus === 'running' && this.boostLeft.magnet > 0) this.pullCoins();
    // A critter treat keeps every critter munching where it is.
    if (this.currentStatus === 'running' && this.boostLeft.treat === 0) {
      for (const index of this.critters.update(this.currentTick, this.critterWorld)) {
        this.events.push({ type: 'critterShooed', tick: this.currentTick, index });
      }
    }
    return this.events.drain();
  }

  /**
   * Head to head: one tick for every seat. Each seat steers and moves in turn, but what it may drive
   * into is judged by where every mower stood as the tick began (and the tiles others set off for
   * this tick), so no seat goes first. Then the match's end is checked.
   */
  private stepMatch(): SimEvent[] {
    if (this.currentStatus !== 'running') return this.events.drain();
    this.currentTick++;
    this.claims.clear();
    this.standing = this.drivers.map((d, i) => {
      let cross = 0;
      this.inSeat(i, () => (cross = this.crossTicks()));
      return { x: d.m.x, y: d.m.y, dir: d.m.dir, progress: d.m.progress, cross };
    });
    // On a soccer pitch the seats take turns going first, tick by tick, so neither always reaches
    // the ball first when both arrive together.
    const order = this.drivers.map((_, i) => i);
    const contested = !!this.level.soccer || !!this.level.roundup;
    if (contested && this.currentTick % 2 === 1) order.reverse();
    order.forEach((i) => {
      const d = this.drivers[i]!;
      if (d.status !== 'running') return;
      // Dizzy: spinning where it stands. Steering waits in its inbox, applied as it wakes.
      if (d.dizzy > 0) {
        if (--d.dizzy === 0) d.steady = STEADY_TICKS;
        return;
      }
      if (d.steady > 0) d.steady--;
      this.inSeat(i, () => {
        tickTurnRule(this.m);
        if (this.m.invulnTicks > 0) this.m.invulnTicks--;
        for (const intent of this.inbox) {
          if (intent.type === 'throttle') {
            if (this.mowerSpec.throttle) this.m.slow = intent.slow;
          } else if (intent.type === 'face' || intent.type === 'turn') this.receiveIntent(intent);
        }
        this.inbox.length = 0;
        this.move();
      });
    });
    // Bumped in the side this tick: dizzy from the next (whichever seat moved first).
    for (const seat of this.dazed) {
      const d = this.drivers[seat]!;
      if (d.status !== 'running') continue;
      d.dizzy = DIZZY_TICKS;
      this.events.push({ type: 'dizzy', tick: this.currentTick, x: d.m.x, y: d.m.y, seat });
    }
    this.dazed.length = 0;
    if (this.level.soccer) this.updateSoccer();
    // A round-up: the sheep graze and drift, keeping clear of every mower.
    if (this.level.roundup) this.critters.update(this.currentTick, this.critterWorld);
    this.checkMatchEnd();
    return this.events.drain();
  }

  /**
   * Head to head: before this seat's mower sets off from a tile center, whether another mower holds
   * the tile ahead (as the tick began) or set off for it this tick. If so it turns back where it
   * stands, a bump with no heart lost, and so does a mower that set off for the same tile this
   * tick. Two mowers driving at each other both turn, as each finds the other in its way. A mower
   * in the way that was crossing this one's path (bumped in the side) is left dizzy.
   */
  private seatInTheWay(): boolean {
    const m = this.m;
    const x = m.x + dx(m.dir);
    const y = m.y + dy(m.dir);
    const key = y * this.grid.width + x;
    const holder = this.standing.findIndex((s, i) => i !== this.seatIndex && holds(s, x, y));
    const other = this.grid.inBounds(x, y) ? this.claims.get(key) : undefined;
    if (holder < 0 && other === undefined) {
      if (this.grid.inBounds(x, y)) this.claims.set(key, this.seatIndex);
      return false;
    }
    if (holder >= 0) this.bumpInTheSide(holder);
    if (other !== undefined) this.inSeat(other, () => this.turnBack(true));
    this.turnBack(false);
    return true;
  }

  /**
   * Head to head: this seat's mower drove into `seat`'s. If that one was crossing its path (driving
   * at right angles to it as the tick began), it is bumped in the side and left dizzy, unless it is
   * already, or only just woke ({@link STEADY_TICKS}). Head-on and from behind are plain bumps.
   */
  private bumpInTheSide(seat: number): void {
    const them = this.standing[seat]!.dir;
    const side = dx(them) * dx(this.m.dir) + dy(them) * dy(this.m.dir) === 0;
    const d = this.drivers[seat]!;
    // (Two against two: no tackling your own side; a bump between teammates is a plain bump.)
    const teammate = this.teams && seat % 2 === this.seatIndex % 2;
    if (!side || teammate || d.dizzy > 0 || d.steady > 0 || this.dazed.includes(seat)) return;
    this.dazed.push(seat);
  }

  /** Head to head: a bump; the mower turns round where it stands (`setOff`: it had just left it). */
  private turnBack(setOff: boolean): void {
    const m = this.m;
    if (setOff) m.progress = 0;
    m.brink = false;
    m.dir = opposite(m.dir);
    recordBounce(m);
    this.clearIntent();
    this.events.push({ type: 'bumped', tick: this.currentTick, x: m.x, y: m.y, dir: m.dir });
  }

  /**
   * Head to head: over when a mower breaks down (the others win; for two, the other), when one has
   * more than half the grass (it cannot be caught), or when the lawn is done (most grass wins).
   */
  private checkMatchEnd(): void {
    if (this.matchEnd) return;
    const soccer = this.level.soccer;
    if (soccer && this.teams) {
      // Two against two (docs/PARTY.md): a team's goals are shared; a team whose mowers have all
      // broken down loses; else the goals that win, or the whistle.
      const teamGoals = [this.seatGoals[0] ?? 0, this.seatGoals[1] ?? 0];
      const playing = [0, 1].filter((team) =>
        this.drivers.some((d, i) => i % 2 === team && d.status === 'running'),
      );
      const leader = teamGoals[0] === teamGoals[1] ? null : teamGoals[0]! > teamGoals[1]! ? 0 : 1;
      if (playing.length < 2) {
        this.endMatch(playing.length === 1 ? playing[0]! : leader, 'brokeDown');
      } else if (teamGoals.some((g) => g >= soccer.target)) {
        this.endMatch(leader, 'goals');
      } else if (soccer.matchTicks !== undefined && this.currentTick >= soccer.matchTicks) {
        this.endMatch(leader, 'time');
      }
      return;
    }
    if (soccer) {
      // A soccer match: a mower breaking down loses; else the goals that win, or the whistle.
      const going = this.drivers.flatMap((d, i) => (d.status === 'running' ? [i] : []));
      const goals = this.drivers.map((_, i) => this.seatGoals[i] ?? 0);
      const best = Math.max(...goals);
      const ahead = goals.flatMap((g, i) => (g === best ? [i] : []));
      const leader = ahead.length === 1 ? ahead[0]! : null;
      if (going.length < this.drivers.length) {
        this.endMatch(going.length === 1 ? going[0]! : leader, 'brokeDown');
      } else if (goals.some((g) => g >= soccer.target)) {
        this.endMatch(leader, 'goals');
      } else if (soccer.matchTicks !== undefined && this.currentTick >= soccer.matchTicks) {
        this.endMatch(leader, 'time');
      }
      return;
    }
    const maze = this.level.maze;
    if (maze) {
      // A maze: a mower breaking down loses; else the first onto the plaza, or at the whistle the
      // one closer to it along the paths (the same distance, or both in at once: a draw).
      const going = this.drivers.flatMap((d, i) => (d.status === 'running' ? [i] : []));
      const plaza = new Set(maze.plaza.map((p) => p.y * this.grid.width + p.x));
      const inside = this.drivers.flatMap(({ m }, i) =>
        plaza.has(m.y * this.grid.width + m.x) ? [i] : [],
      );
      const closer = (): number | null => {
        const dist = this.mazeDistances;
        const best = Math.min(...dist);
        const ahead = dist.flatMap((n, i) => (n === best ? [i] : []));
        return ahead.length === 1 ? ahead[0]! : null;
      };
      // (A party of three or four: a mower that broke down is out and the rest go on, docs/PARTY.md.)
      const party = this.drivers.length > 2;
      if (party ? going.length <= 1 : going.length < this.drivers.length) {
        this.endMatch(going.length === 1 ? going[0]! : closer(), 'brokeDown');
      } else if (inside.length > 0) {
        this.endMatch(inside.length === 1 ? inside[0]! : null, 'maze');
      } else if (this.currentTick >= maze.matchTicks) {
        this.endMatch(closer(), 'time');
      }
      return;
    }
    const roundup = this.level.roundup;
    if (roundup) {
      // A round-up: a mower breaking down loses; else the sheep that win, or the whistle.
      const going = this.drivers.flatMap((d, i) => (d.status === 'running' ? [i] : []));
      const penned = this.penCounts;
      const best = Math.max(...penned);
      const ahead = penned.flatMap((n, i) => (n === best ? [i] : []));
      const leader = ahead.length === 1 ? ahead[0]! : null;
      // (A party of three or four: a mower that broke down is out and the rest go on, docs/PARTY.md.)
      const party = this.drivers.length > 2;
      if (party ? going.length <= 1 : going.length < this.drivers.length) {
        this.endMatch(going.length === 1 ? going[0]! : leader, 'brokeDown');
      } else if (penned.some((n) => n >= roundup.target)) {
        this.endMatch(leader, 'sheep');
      } else if (this.currentTick >= roundup.matchTicks) {
        this.endMatch(leader, 'time');
      }
      return;
    }
    const tiles = this.drivers.map((d) => d.tiles);
    const most = (): number | null => {
      const best = Math.max(...tiles);
      const top = tiles.flatMap((t, i) => (t === best ? [i] : []));
      return top.length === 1 ? top[0]! : null;
    };
    const going = this.drivers.flatMap((d, i) => (d.status === 'running' ? [i] : []));
    if (this.drivers.length > 2) {
      // A party mowing (docs/PARTY.md): a mower that breaks down is out and the rest play on, until
      // one is left; the leader wins once nobody can catch them, or the lawn is done.
      if (going.length <= 1) {
        this.endMatch(going[0] ?? most(), 'brokeDown');
        return;
      }
      const ranked = [...tiles].sort((a, b) => b - a);
      if (ranked[0]! - ranked[1]! > this.grid.grassRemaining) this.endMatch(most(), 'lead');
      else if (this.grid.grassRemaining === 0) this.endMatch(most(), 'done');
      return;
    }
    if (going.length < this.drivers.length) {
      if (going.length === 1) this.endMatch(going[0]!, 'brokeDown');
      else if (going.length === 0) this.endMatch(most(), 'brokeDown');
      if (this.matchEnd) return;
    }
    const half = tiles.findIndex((t) => t * 2 > this.grid.totalGrass);
    if (half >= 0) this.endMatch(half, 'half');
    else if (this.grid.grassRemaining === 0) this.endMatch(most(), 'done');
  }

  private endMatch(winner: number | null, why: MatchResult['why']): void {
    this.matchEnd = { winner, why };
    this.currentStatus = 'won';
    this.events.push({ type: 'matchOver', tick: this.currentTick, winner, why });
  }

  /**
   * Head to head: `fn`'s answer as seat `seat` sees things: `mower`, `crossTicks()`, the turn
   * preview and the like are that seat's (for drawing the other mower, or this phone's own when it
   * is not seat 0). Read only: nothing it does should change the match.
   */
  asSeat<T>(seat: number, fn: () => T): T {
    if (seat === this.seatIndex || !this.drivers[seat]) return fn();
    let out!: T;
    this.inSeat(seat, () => (out = fn()));
    return out;
  }

  /** Head to head: the seat that mowed (x, y), or -1 (not mowed, or a lawn without seats). */
  ownerAt(x: number, y: number): number {
    return this.owners?.[y * this.grid.width + x] ?? -1;
  }

  /** Head to head: how many mowers are on the lawn (1 without seats). */
  get seats(): number {
    return this.drivers.length;
  }

  /** Head to head: a seat's mower. */
  mowerOf(seat: number): Readonly<MowerState> | undefined {
    return this.drivers[seat]?.m;
  }

  /** Head to head: grass tiles a seat has taken so far. */
  seatTiles(seat: number): number {
    return this.drivers[seat]?.tiles ?? 0;
  }

  /** Head to head: coins a seat has picked up so far. */
  seatCoins(seat: number): number {
    return this.drivers[seat]?.coins ?? 0;
  }

  /** Head to head: whether a seat's mower is still going (`running`), broken or out of gas. */
  seatStatus(seat: number): SimStatus {
    return this.drivers[seat]?.status ?? 'running';
  }

  /**
   * Head to head: ticks this mower has left spinning, dizzy from a bump in the side (0: not).
   * Through `asSeat` or `seatView`, that seat's.
   */
  get dizzy(): number {
    return this.drivers[this.seatIndex]!.dizzy;
  }

  /** Head to head: ticks a seat's mower has left dizzy (0: not). */
  seatDizzy(seat: number): number {
    return this.drivers[seat]?.dizzy ?? 0;
  }

  /** Head to head: ticks left in which a seat's mower, just woken, cannot be made dizzy again. */
  seatSteady(seat: number): number {
    return this.drivers[seat]?.steady ?? 0;
  }

  /** A maze match: tiles to the plaza from each tile (made once, on first asking). */
  private mazeMap: Int32Array | null = null;

  /** Head to head: a seat's steering requests, stamped with the tick each applied on. */
  seatLog(seat: number): readonly LoggedIntent[] {
    return this.drivers[seat]?.log ?? [];
  }

  /** Head to head: how the match ended, or null while it goes on (and on lawns without seats). */
  get match(): Readonly<MatchResult> | null {
    if (!this.matchEnd) return null;
    return {
      ...this.matchEnd,
      tiles: this.drivers.map((d) => d.tiles),
      coins: this.drivers.map((d) => d.coins),
      ends: this.drivers.map((d) => d.status),
      ...(this.level.soccer ? { goals: [...this.soccerGoals] } : {}),
      ...(this.level.roundup ? { penned: [...this.penCounts] } : {}),
      ...(this.level.maze ? { distance: [...this.mazeDistances] } : {}),
    };
  }

  /**
   * A maze match: each seat's tiles from the plaza along the paths, from the tile its mower stands
   * on (empty on other lawns). Unreachable counts as very far.
   */
  get mazeDistances(): readonly number[] {
    const maze = this.level.maze;
    if (!maze) return [];
    const w = this.grid.width;
    if (!this.mazeMap) {
      // Out from the plaza through every tile the loaner can drive (the hedges never change).
      const far = 1_000_000;
      const map = new Int32Array(w * this.grid.height).fill(far);
      const queue: number[] = [];
      for (const p of maze.plaza) {
        map[p.y * w + p.x] = 0;
        queue.push(p.y * w + p.x);
      }
      const { cutPower, breakPower = 0 } = this.mowerSpec;
      for (let q = 0; q < queue.length; q++) {
        const i = queue[q]!;
        const x = i % w;
        const y = Math.floor(i / w);
        for (const [nx, ny] of [
          [x + 1, y],
          [x - 1, y],
          [x, y + 1],
          [x, y - 1],
        ] as const) {
          if (!this.grid.inBounds(nx, ny) || this.grid.isBlockedFor(nx, ny, cutPower, breakPower)) {
            continue;
          }
          const j = ny * w + nx;
          if (map[j]! <= map[i]! + 1) continue;
          map[j] = map[i]! + 1;
          queue.push(j);
        }
      }
      this.mazeMap = map;
    }
    const map = this.mazeMap;
    return this.drivers.map(({ m }) => map[m.y * w + m.x] ?? 1_000_000);
  }

  /** A round-up match: the sheep in each seat's pen so far (empty on other lawns). */
  get penCounts(): readonly number[] {
    if (!this.level.roundup) return [];
    return this.drivers.map(
      (_, seat) => this.critters.list.filter((c) => c.penned && c.pen === seat).length,
    );
  }

  /** A round-up match: a seat's own pen and gate (null on other lawns). */
  homePen(seat: number): HerdSpec | null {
    return this.level.roundup?.pens[seat] ?? null;
  }

  /**
   * A soccer match: each seat's goals so far (empty on other lawns); two against two, each seat
   * its team's (a goal is credited to the team's first seat, 0 or 1).
   */
  get soccerGoals(): readonly number[] {
    if (!this.level.soccer || this.drivers.length < 2) return [];
    return this.drivers.map((_, i) => this.seatGoals[this.teams ? i % 2 : i] ?? 0);
  }

  /** Soccer two against two (docs/PARTY.md): seats 0 and 2 against 1 and 3. */
  get teams(): boolean {
    return !!this.level.soccer && this.drivers.length === 4;
  }

  /**
   * A soccer match: the goal mouth tiles a seat attacks (the far end from where it started). On a
   * lawn without seats, every mouth.
   */
  attackedMouths(seat: number): readonly { x: number; y: number }[] {
    const soccer = this.level.soccer;
    if (!soccer) return [];
    if (this.drivers.length < 2) return soccer.mouths;
    const start = seat === 0 ? this.level.start : this.level.seats?.[seat];
    const fromBottom = (start?.y ?? 0) > soccer.center.y;
    return soccer.mouths.filter((p) =>
      fromBottom ? p.y < soccer.center.y : p.y > soccer.center.y,
    );
  }

  /**
   * Head to head: a steering request for a seat's mower, applied during the next step. Only
   * steering and the throttle count; nothing once the match or that mower is done.
   */
  enqueueFor(seat: number, intent: Intent): void {
    const d = this.drivers[seat];
    if (!d || this.currentStatus !== 'running' || d.status !== 'running') return;
    if (intent.type !== 'face' && intent.type !== 'turn' && intent.type !== 'throttle') return;
    d.inbox.push(intent);
    d.log.push({ tick: this.currentTick + 1, intent });
  }

  /**
   * Continue after breaking or running dry: full health and tank where the mower stopped, a moment
   * of crash immunity, and free to face any open way (as on its start tile). Takes a tick without
   * moving; anything else queued with it is dropped.
   */
  private revive(): void {
    this.currentTick++;
    this.inbox.length = 0;
    const m = this.m;
    this.currentStatus = 'running';
    m.hp = this.mowerSpec.maxHp;
    m.fuel = this.mowerSpec.fuelTiles ?? Number.POSITIVE_INFINITY;
    m.flat = false;
    m.departed = false;
    m.invulnTicks = CRASH_INVULN_TICKS;
    this.clearIntent();
    this.events.push({ type: 'revived', tick: this.currentTick, x: m.x, y: m.y });
  }

  /** Boss storm: caught when the rain reaches any part of the mower (its tile or the next). */
  private updateStorm(): void {
    const row = this.stormRow();
    if (row === null || row >= this.grid.height) return;
    const m = this.m;
    const tiles = this.footprintAt(m.x, m.y, m.dir);
    if (tiles.some((p) => p.y >= row)) {
      this.currentStatus = 'caught';
      this.events.push({ type: 'caught', tick: this.currentTick, x: m.x, y: m.y });
    }
  }

  // ── Tools ─────────────────────────────────────────────────────────────────────────────────

  /** Ticks a boost has left to run (0 when off). */
  boostTicks(boost: BoostId): number {
    return this.boostLeft[boost];
  }

  /** Whether a boost has been fired this attempt (each kind works once). */
  boostUsed(boost: BoostId): boolean {
    return this.boostsUsed.has(boost);
  }

  private useBoost(boost: BoostId): void {
    if (this.boostsUsed.has(boost)) return;
    this.boostsUsed.add(boost);
    this.boostLeft[boost] = BOOST_TICKS[boost];
    this.events.push({ type: 'boostUsed', tick: this.currentTick, boost });
  }

  /** How many of an item are held (picked up from boxes and not yet fired). */
  heldItem(item: ItemId): number {
    return this.held[item];
  }

  /** Ticks before a held trimmer cut can be fired (after the last one). */
  get trimWait(): number {
    return this.trimTicks;
  }

  /**
   * Fires a held item: Turbo runs the boost's burst (not while one is running), a trimmer cut
   * swings the String Trimmer once at its first reach (not during its cooldown). Nothing held,
   * nothing happens.
   */
  private useItem(item: ItemId): void {
    if (this.held[item] <= 0) return;
    if (item === 'turbo') {
      if (this.boostLeft.turbo > 0) return;
      this.held.turbo--;
      this.boostLeft.turbo = BOOST_TICKS.turbo;
      this.events.push({
        type: 'boostUsed',
        tick: this.currentTick,
        boost: 'turbo',
        fromBox: true,
      });
      return;
    }
    if (this.trimTicks > 0) return;
    this.held.trim--;
    this.trimTicks = ITEM_TRIMMER.cooldownTicks;
    this.applyTool(ITEM_TRIMMER, -1);
  }

  private tickBoosts(): void {
    for (const id of BOOST_IDS) if (this.boostLeft[id] > 0) this.boostLeft[id]--;
  }

  /** Coin magnet: every coin within reach of the mower comes to it. */
  private pullCoins(): void {
    const m = this.m;
    const pickups = this.level.pickups ?? [];
    for (let i = 0; i < pickups.length; i++) {
      const p = pickups[i]!;
      if (p.kind !== 'coin' || this.collected[i]) continue;
      if (Math.max(Math.abs(p.x - m.x), Math.abs(p.y - m.y)) > MAGNET_REACH) continue;
      this.collectPickups(p.x, p.y, true);
    }
  }

  private useTool(slot: number): void {
    const spec = this.tools[slot];
    const state = this.toolStates[slot];
    if (!spec || !state) return;
    if (state.charges <= 0 || state.cooldown > 0) {
      const reason = state.charges <= 0 ? 'empty' : 'cooldown';
      this.events.push({ type: 'toolDenied', tick: this.currentTick, slot, reason });
      return;
    }
    state.charges--;
    state.cooldown = spec.cooldownTicks;
    this.applyTool(spec, slot);
  }

  /** What a tool does once fired (`slot` -1: a trimmer cut from an item box). */
  private applyTool(spec: ToolSpec, slot: number): void {
    const m = this.m;
    const cleared: { x: number; y: number }[] = [];
    if (spec.clears.length > 0 || spec.flattens || spec.trims) {
      for (const p of this.toolReach(spec)) {
        if (!this.grid.inBounds(p.x, p.y)) continue;
        if (spec.flattens && this.grid.tileAt(p.x, p.y) === Tile.Molehill) {
          this.mowAt(p.x, p.y);
          cleared.push(p);
          continue;
        }
        // The string trimmer cuts grass right down, however tall, without driving onto it.
        if (spec.trims && needsMowing(this.grid.tileAt(p.x, p.y))) {
          this.mowAt(p.x, p.y, TRIM_BITE);
          cleared.push(p);
          continue;
        }
        if (spec.only && !spec.only.includes(p.y * this.grid.width + p.x)) continue;
        if (this.grid.clear(p.x, p.y, spec.clears)) cleared.push(p);
      }
    }
    if (spec.shutsOff) {
      // The sprinkler key: every head within reach turned off (no spray, nothing to break).
      for (let y = m.y - spec.range; y <= m.y + spec.range; y++) {
        for (let x = m.x - spec.range; x <= m.x + spec.range; x++) {
          if (this.grid.clear(x, y, [Tile.Sprinkler])) cleared.push({ x, y });
        }
      }
    }
    const shooed: number[] = [];
    if (spec.sendsHome) shooed.push(...this.critters.sendHome(spec.sendsHome, this.currentTick));
    if (spec.shoos) {
      this.critters.list.forEach((c, i) => {
        if (c.inside || Math.abs(c.x - m.x) + Math.abs(c.y - m.y) > spec.range) return;
        if (this.critters.shoo(i, m.x, m.y, this.currentTick, this.critterWorld)) shooed.push(i);
      });
    }
    this.events.push({
      type: 'toolUsed',
      tick: this.currentTick,
      slot,
      id: spec.id,
      cleared,
      shooed,
    });
    for (const index of shooed) {
      this.events.push({ type: 'critterShooed', tick: this.currentTick, index });
    }
  }

  /**
   * The tiles a tool works on: the lane ahead of the next decision tile (the tile being entered is
   * already known clear), or for a tool that works all round, every tile within its range of the
   * tile the mower is mostly on (that one included). May run off the lawn.
   */
  private toolReach(spec: ToolSpec): { x: number; y: number }[] {
    const m = this.m;
    const out: { x: number; y: number }[] = [];
    if (spec.around) {
      const ahead = this.nextDecisionTile();
      const c = m.progress * 2 >= this.crossTicks() ? ahead : { x: m.x, y: m.y };
      for (let y = c.y - spec.range; y <= c.y + spec.range; y++) {
        for (let x = c.x - spec.range; x <= c.x + spec.range; x++) out.push({ x, y });
      }
      // One tile further, straight or diagonally, over whatever the mower can't or shouldn't
      // drive on, so grass hemmed in behind a hose or a flower bed can still be trimmed.
      const r = spec.range;
      for (let ddy = -1; ddy <= 1; ddy++) {
        for (let ddx = -1; ddx <= 1; ddx++) {
          if (ddx === 0 && ddy === 0) continue;
          const bx = c.x + ddx * r;
          const by = c.y + ddy * r;
          if (this.grid.inBounds(bx, by) && trimmerReachesOver(this.grid.tileAt(bx, by))) {
            out.push({ x: c.x + ddx * (r + 1), y: c.y + ddy * (r + 1) });
          }
        }
      }
      return out;
    }
    const d = this.nextDecisionTile();
    for (let k = 1; k <= spec.range; k++) {
      out.push(...this.footprintAt(d.x + dx(m.dir) * k, d.y + dy(m.dir) * k, m.dir));
    }
    return out;
  }

  private makeCritterWorld(): CritterWorld {
    const canStand = (x: number, y: number) => {
      const t = this.grid.tileAt(x, y);
      return (
        this.grid.inBounds(x, y) &&
        !isSolid(t) &&
        t !== Tile.Brush &&
        t !== Tile.Ornament &&
        t !== Tile.Hose &&
        t !== Tile.HoseChewed &&
        t !== Tile.Sprinkler &&
        t !== Tile.Thorns
      );
    };
    return {
      canStand,
      openNeighbors: (x, y) => DIRECTIONS.filter((d) => canStand(x + dx(d), y + dy(d))).length,
      width: this.grid.width,
      height: this.grid.height,
      // Fairness: never step onto the mower, its next decision tile, or anywhere it could turn into
      // from there. A critter can't appear in the mower's path faster than a player can react.
      pens: this.level.roundup?.pens ?? (this.level.herd ? [this.level.herd] : []),
      threat: () => this.nextDecisionTile(),
      // A round-up match: every mower counts (the sheep keep clear of each, never pushed onto one).
      ...(this.level.roundup
        ? {
            threats: () =>
              this.drivers.map((_, i) => this.asSeat(i, () => this.nextDecisionTile())),
            mowerIn: (x: number, y: number) =>
              this.drivers.some(({ m }, i) => {
                if (i === this.seatIndex) return false;
                if (m.x === x && m.y === y) return true;
                return m.progress > 0 && m.x + dx(m.dir) === x && m.y + dy(m.dir) === y;
              }),
          }
        : {}),
      forbidden: (x, y) =>
        this.level.roundup
          ? this.drivers.some((_, i) => this.asSeat(i, () => this.nearMower(x, y)))
          : this.nearMower(x, y),
    };
  }

  /** Whether (x, y) is on this mower, its next decision tile, or anywhere it could turn into. */
  private nearMower(x: number, y: number): boolean {
    const m = this.m;
    if (this.footprintAt(m.x, m.y, m.dir).some((p) => p.x === x && p.y === y)) return true;
    const d = this.nextDecisionTile();
    if (this.cutWidth === 1) return Math.abs(x - d.x) + Math.abs(y - d.y) <= 1;
    // Wide mowers can pivot into any tile around their footprint.
    return this.footprintAt(d.x, d.y, m.dir).some(
      (p) => Math.abs(x - p.x) <= 1 && Math.abs(y - p.y) <= 1,
    );
  }

  // ── Steering ──────────────────────────────────────────────────────────────────────────────

  /**
   * Press and hold (spinning mowers only): park at a tile center and spin there until steered.
   * A hold takes a moment to recognize, about a tile at the Zero-Turn's speed (owner, 2026-10-10:
   * "a tile too late"), so it parks at the center the mower has just left when it can:
   * - nosing toward an obstacle (the brink), it parks where it stands instead of crashing;
   * - with a turn still waiting for the next center, it makes the turn there, then parks;
   * - otherwise it backs up to the center it left if it is less than halfway across, or anywhere
   *   across after turning there (a turn, then a hold, means stop at that corner).
   */
  private requestPark(): void {
    const m = this.m;
    if (this.mowerSpec.turnRule.kind !== 'freeSpin' || m.parked) return;
    m.parkRequested = true;
    if (m.brink) {
      m.progress = 0;
      m.brink = false;
      return;
    }
    if (m.progress === 0 || m.pendingIntent || m.backing) return;
    const justTurned = m.tilesSinceTurn === 0 && m.lastTurn !== null;
    if (justTurned || m.progress * 2 <= this.crossTicks()) m.backing = true;
  }

  private receiveIntent(intent: SteerIntent): void {
    const m = this.m;
    // A steer after a hold that has not parked yet: the newer request wins (it drives on). A
    // mower backing up to park still gets back to its center first.
    if (!m.parked) m.parkRequested = false;
    if (m.pendingIntent && this.followsPending(intent)) {
      // A quick combo (right, then down for a U-turn): keep both, in order. A newer follow-up
      // replaces an older one.
      m.queuedIntent = intent;
      return;
    }
    // Anything else is a fresh request or a correction of the pending one.
    m.pendingIntent = intent;
    m.queuedIntent = null;
    m.intentCentersLeft = INTENT_BUFFER_CENTERS;

    // Cornering grace: a request that lands just after leaving a center still turns there (a
    // longer grace when the mower is nosing toward an obstacle).
    const grace = m.brink
      ? this.brinkTicks()
      : Math.min(TURN_GRACE_MAX_TICKS, Math.floor(this.mowerSpec.ticksPerTile / 4));
    if (m.progress > 0 && m.progress <= grace && !m.backing) {
      const turn = this.resolveTurn(intent);
      if (turn && this.turnAllowed(turn) && this.turnTargetOpen(turn) && !this.slippery()) {
        m.progress = 0;
        m.brink = false;
        this.performTurn(turn);
        this.clearIntent();
      }
    }
  }

  /**
   * Whether a request arriving while another is pending is its follow-up rather than a correction:
   * a heading that is a quarter turn from where the pending request will leave the mower, but not
   * from where it is now (from north, 'east' then 'south' is a U-turn; 'east' then 'west' is a
   * change of mind). Relative turns (tap controls) always chain: each tap is one more turn.
   */
  private followsPending(intent: SteerIntent): boolean {
    const pending = this.m.pendingIntent;
    if (!pending || pending.type !== intent.type) return false;
    if (intent.type === 'turn') return true;
    if (pending.type !== 'face' || intent.dir === pending.dir) return false;
    const fromAfter = turnBetween(pending.dir, intent.dir);
    return fromAfter === 'left' || fromAfter === 'right';
  }

  private resolveTurn(intent: SteerIntent): Turn | null {
    return intent.type === 'turn' ? intent.turn : turnBetween(this.m.dir, intent.dir);
  }

  private turnAllowed(turn: Turn): boolean {
    // Before it sets off, the mower is being placed: any facing is fine (still not into a wall).
    if (!this.m.departed) return true;
    return canTurn(this.mowerSpec.turnRule, this.m, turn);
  }

  private turnTargetOpen(turn: Turn): boolean {
    const m = this.m;
    return this.canTurnInto(m.x, m.y, m.dir, applyTurn(m.dir, turn));
  }

  private performTurn(turn: Turn): void {
    const m = this.m;
    const from = m.dir;
    m.dir = applyTurn(m.dir, turn);
    if (turn === 'reverse') this.flipSide();
    // Choosing the starting heading does not count toward turn rules (spacing, pull-back).
    if (m.departed) recordTurn(this.mowerSpec.turnRule, m, turn);
    this.events.push({ type: 'turned', tick: this.currentTick, x: m.x, y: m.y, turn, dir: m.dir });
    // A wide mower's second track swings onto new grass as it pivots; side reels sweep round.
    if (this.cutWidth > 1) this.arriveAt(m.x, m.y);
    if (this.mowerSpec.wings) this.sweepWings(m.x, m.y, from, m.dir);
  }

  /**
   * Reversing a wide mower backs it up over the same two tiles, so its second track switches
   * relative side (left of east is the same tile as right of west).
   */
  private flipSide(): void {
    if (this.cutWidth > 1) this.m.side = this.m.side === 'left' ? 'right' : 'left';
  }

  /** Drops every buffered request. */
  private clearIntent(): void {
    this.m.pendingIntent = null;
    this.m.queuedIntent = null;
    this.m.intentCentersLeft = 0;
  }

  /** The pending request is done with: its follow-up (if any) becomes pending, fresh. */
  private advanceIntent(): void {
    const m = this.m;
    m.pendingIntent = m.queuedIntent;
    m.queuedIntent = null;
    m.intentCentersLeft = m.pendingIntent ? INTENT_BUFFER_CENTERS : 0;
  }

  /** At a tile center: apply the buffered intent if possible, otherwise age it. */
  private resolvePendingAtCenter(): void {
    const intent = this.m.pendingIntent;
    if (!intent) return;
    const turn = this.resolveTurn(intent);
    if (turn === null) {
      // Already facing that way; a queued follow-up may apply right here.
      this.advanceIntent();
      if (this.m.pendingIntent) this.resolvePendingAtCenter();
      return;
    }
    // On a wet tile the mower slides on: the request waits for the next dry center (it doesn't
    // use up its buffer).
    if (this.slippery()) return;
    const allowed = this.turnAllowed(turn);
    if (allowed && this.turnTargetOpen(turn)) {
      this.performTurn(turn);
      this.advanceIntent(); // a follow-up waits for the next center
      return;
    }
    this.m.intentCentersLeft--;
    if (this.m.intentCentersLeft <= 0) {
      this.events.push({
        type: 'turnRejected',
        tick: this.currentTick,
        turn,
        reason: allowed ? 'blocked' : 'rule',
      });
      this.clearIntent();
    }
  }

  // ── Movement, mowing, collisions ──────────────────────────────────────────────────────────

  private move(): void {
    const m = this.m;
    if (m.backing) {
      // Reversing onto the center it left (requestPark): it parks there unless steered meanwhile.
      m.progress--;
      if (m.progress > 0) return;
      m.progress = 0;
      m.backing = false;
      if (!m.pendingIntent) {
        m.parkRequested = true;
        this.spinInPlace();
        return;
      }
      m.parked = true; // steered while backing: leaves from the center as from a park
    }
    if (m.brink) {
      if (this.canOccupy(m.x + dx(m.dir), m.y + dy(m.dir), m.dir)) {
        // Whatever was in the way moved off (a critter): drive on.
        m.brink = false;
      } else if (m.progress >= this.brinkTicks()) {
        // No turn came: back to the center, and the crash.
        m.progress = 0;
        m.brink = false;
        this.crash();
        return;
      } else {
        m.progress++;
        return;
      }
    }
    if (m.progress === 0) {
      // Parked (press and hold): spin in place until a steering request picks the way out.
      const parking = m.parked || m.parkRequested;
      if (parking && !m.pendingIntent) {
        this.spinInPlace();
        return;
      }
      if (m.parkRequested && !m.parked) {
        // A turn, then a hold before the turn was made (swipe, then hold): turn here, then park.
        this.resolvePendingAtCenter();
        this.clearIntent();
        this.spinInPlace();
        return;
      }
      const pending = m.pendingIntent;
      // Steering the way it already faces sets off; a turn it can't make leaves it spinning.
      const alreadyFacing = parking && pending !== null && this.resolveTurn(pending) === null;
      const before = m.dir;
      this.resolvePendingAtCenter();
      if (parking) {
        const turned = m.dir !== before;
        // Leaving a park: only into the open (a swipe at a wall just keeps it spinning).
        if (
          (!turned && !alreadyFacing) ||
          !this.canOccupy(m.x + dx(m.dir), m.y + dy(m.dir), m.dir)
        ) {
          this.clearIntent();
          this.spinInPlace();
          return;
        }
        m.parked = false;
        m.parkRequested = false;
        m.parkTicks = 0;
      }
      // Head to head: another mower in the way is a bump (it never drives through).
      if (this.drivers.length > 1 && this.seatInTheWay()) return;
      // A squirrel or rabbit in the way poofs off the lawn (rules 6): no crash, on it drives.
      const blocked =
        !this.canOccupy(m.x + dx(m.dir), m.y + dy(m.dir), m.dir) &&
        !(this.poofSmallCritters() && this.canOccupy(m.x + dx(m.dir), m.y + dy(m.dir), m.dir));
      if (blocked) {
        // A sheep in the way is nudged on (forced out if cornered); one that can't budge at all is
        // waited behind. Bumping a sheep never costs a heart.
        if (this.sheepAhead()) {
          if (!this.nudgeSheep()) return;
        } else if (this.ballAhead()) {
          if (!this.kickBall()) return;
        } else if (this.brinkTicks() > 0) {
          // The turn grace: nose on toward the obstacle a moment before crashing (see `brink`).
          m.brink = true;
          m.progress++;
          return;
        } else {
          this.crash();
          return;
        }
      }
    }
    m.progress++;
    m.departed = true;
    if (m.progress >= this.crossTicks()) {
      const uphill = this.climbAt(m.x, m.y, m.dir) === 'up';
      m.x += dx(m.dir);
      m.y += dy(m.dir);
      m.progress = 0;
      recordTileTraveled(m);
      if (Number.isFinite(m.fuel)) m.fuel--;
      // A pass through tall grass burns extra (after any gas can on this tile refilled the tank).
      const heavy = this.arriveAt(m.x, m.y);
      // Boss lawn art: driving onto the judges' table with the whole lawn mowed is the win.
      const art = this.level.art;
      if (art?.goal && m.x === art.goal.x && m.y === art.goal.y && this.grid.grassRemaining === 0) {
        if (this.currentStatus === 'running') {
          this.currentStatus = 'won';
          this.events.push({ type: 'levelComplete', tick: this.currentTick });
        }
      }
      // Boss storm: reaching the top row (the shed) is the win.
      if (this.level.storm && m.y === 0 && this.currentStatus === 'running') {
        this.currentStatus = 'won';
        this.events.push({ type: 'levelComplete', tick: this.currentTick });
      }
      if (heavy && Number.isFinite(m.fuel)) {
        m.fuel = Math.max(0, m.fuel - TALL_GRASS_EXTRA_FUEL);
      }
      // A climb burns extra too.
      if (uphill && Number.isFinite(m.fuel)) m.fuel = Math.max(0, m.fuel - UPHILL_EXTRA_FUEL);
      if (this.currentStatus === 'running' && m.fuel <= 0) this.runDry();
    }
  }

  /** The tank is empty: the Reserve Can fills it up once a lawn, else the mower stops. */
  private runDry(): void {
    const m = this.m;
    if (m.reserveCan > 0 && this.mowerSpec.fuelTiles !== undefined) {
      m.reserveCan--;
      m.fuel = this.mowerSpec.fuelTiles;
      this.events.push({ type: 'reserveCan', tick: this.currentTick, x: m.x, y: m.y });
      return;
    }
    this.currentStatus = 'outOfFuel';
    this.events.push({ type: 'outOfFuel', tick: this.currentTick, x: m.x, y: m.y });
  }

  /** Parked: spinning on the spot, burning a tile's worth of gas per tile's worth of time. */
  private spinInPlace(): void {
    const m = this.m;
    m.parked = true;
    m.parkRequested = false;
    m.parkTicks++;
    if (!Number.isFinite(m.fuel) || m.parkTicks % this.mowerSpec.ticksPerTile !== 0) return;
    m.fuel--;
    if (this.currentStatus === 'running' && m.fuel <= 0) this.runDry();
  }

  /**
   * Boss herding: a sheep right ahead is nudged a tile on (when there is room beyond it), and the
   * mower carries on instead of crashing. Returns whether the way is now clear.
   */
  /** Mower soccer: whether the ball sits on the tile the mower is about to drive into. */
  private ballAhead(): boolean {
    const m = this.m;
    return this.footprintAt(m.x + dx(m.dir), m.y + dy(m.dir), m.dir).some((p) =>
      this.ballAt(p.x, p.y),
    );
  }

  /**
   * Whether the ball could roll onto (x, y): open lawn, nobody there, and neither under the mower
   * nor just ahead of it (the mower always drives on, so it would only kick it again).
   */
  private ballCanEnter(x: number, y: number): boolean {
    if (!this.grid.inBounds(x, y) || isSolid(this.grid.tileAt(x, y))) return false;
    if (this.grid.tileAt(x, y) === Tile.Brush || this.critters.occupied(x, y)) return false;
    const at = (p: { x: number; y: number }) => p.x === x && p.y === y;
    // Every mower (a soccer match has two): not under it, nor just ahead of it.
    return !this.drivers.some(
      ({ m }) =>
        footprint(m.x, m.y, m.dir, this.cutWidth, m.side).some(at) ||
        footprint(m.x + dx(m.dir), m.y + dy(m.dir), m.dir, this.cutWidth, m.side).some(at),
    );
  }

  /**
   * Mower soccer: the mower drives into the ball and kicks it: it rolls on up to KICK_TILES ahead.
   * Against the fence or something solid it squirts out to one side (the sheep's rule); with no
   * way out at all it pops back to the center spot. Never a crash. Returns whether the way is now
   * clear.
   */
  private kickBall(): boolean {
    const b = this.soccerBall;
    if (!b) return false;
    const m = this.m;
    const ways: [Direction, boolean][] = [
      [m.dir, false],
      [turnLeft(m.dir), true],
      [turnRight(m.dir), true],
    ];
    const way = ways.find(([d]) => this.ballCanEnter(b.x + dx(d), b.y + dy(d)));
    if (!way) {
      this.resetBall();
    } else {
      const [d, squirt] = way;
      b.dir = d;
      b.left = squirt ? SQUIRT_TILES : KICK_TILES;
      b.progress = 0;
      this.events.push({ type: 'kicked', tick: this.currentTick, x: b.x, y: b.y, dir: d, squirt });
      // The ball is off at once (the mower follows into where it was).
      this.rollBall();
    }
    return this.canOccupy(m.x + dx(m.dir), m.y + dy(m.dir), m.dir);
  }

  /** The ball rolls one tile on (scoring in a goal mouth), or stops if it cannot. */
  private rollBall(): void {
    const b = this.soccerBall!;
    const soccer = this.level.soccer!;
    const nx = b.x + dx(b.dir);
    const ny = b.y + dy(b.dir);
    if (b.left <= 0 || !this.ballCanEnter(nx, ny)) {
      b.left = 0;
      return;
    }
    b.x = nx;
    b.y = ny;
    b.left--;
    b.progress = 0;
    if (soccer.mouths.some((p) => p.x === nx && p.y === ny)) {
      this.goalCount++;
      // A match: the goal counts for whoever attacks that end (an own goal for the other).
      const seat =
        this.drivers.length > 1
          ? this.drivers.findIndex((_, i) =>
              this.attackedMouths(i).some((p) => p.x === nx && p.y === ny),
            )
          : -1;
      if (seat >= 0) this.seatGoals[seat] = (this.seatGoals[seat] ?? 0) + 1;
      this.events.push({
        type: 'goal',
        tick: this.currentTick,
        goals: this.goalCount,
        x: nx,
        y: ny,
        ...(seat >= 0 ? { seat } : {}),
      });
      this.resetBall();
    }
  }

  /** The ball back on the center spot (or the nearest free tile to it). */
  private resetBall(): void {
    const soccer = this.level.soccer!;
    const { x: cx, y: cy } = soccer.center;
    let spot = { x: cx, y: cy };
    for (let r = 0; r < 4 && !this.ballCanEnter(spot.x, spot.y); r++) {
      const ring = [
        { x: cx, y: cy - r - 1 },
        { x: cx, y: cy + r + 1 },
        { x: cx - r - 1, y: cy },
        { x: cx + r + 1, y: cy },
      ];
      spot = ring.find((p) => this.ballCanEnter(p.x, p.y)) ?? spot;
    }
    this.soccerBall = { x: spot.x, y: spot.y, dir: 0 as Direction, left: 0, progress: 0 };
    this.events.push({ type: 'ballReset', tick: this.currentTick, x: spot.x, y: spot.y });
  }

  /** Mower soccer each tick: the rolling ball moves on; the whistle ends the half. */
  private updateSoccer(): void {
    const soccer = this.level.soccer;
    const b = this.soccerBall;
    if (!soccer || !b) return;
    if (b.left > 0 && ++b.progress >= BALL_TICKS) this.rollBall();
    // A match ends by its own rules (checkMatchEnd).
    if (this.drivers.length > 1) return;
    if (this.halftimeEta() === 0) {
      const won = this.goalCount >= soccer.target;
      this.currentStatus = won ? 'won' : 'late';
      this.events.push({ type: 'halftime', tick: this.currentTick, won });
      if (won) this.events.push({ type: 'levelComplete', tick: this.currentTick });
      else this.events.push({ type: 'late', tick: this.currentTick });
    }
  }

  /**
   * Rules 6: every squirrel or rabbit on the tiles the mower is about to drive into poofs off the
   * lawn in a puff (back home after {@link POOF_TICKS}). True if any did.
   */
  private poofSmallCritters(): boolean {
    if (!this.rules.softSmallCritters) return false;
    const m = this.m;
    let any = false;
    for (const p of this.footprintAt(m.x + dx(m.dir), m.y + dy(m.dir), m.dir)) {
      const i = this.critters.indexAt(p.x, p.y);
      const kind = i >= 0 ? this.critters.list[i]?.kind : undefined;
      if (kind !== 'squirrel' && kind !== 'rabbit') continue;
      this.critters.poof(i, POOF_TICKS, this.currentTick);
      this.events.push({
        type: 'critterPoofed',
        tick: this.currentTick,
        index: i,
        kind,
        x: p.x,
        y: p.y,
      });
      any = true;
    }
    return any;
  }

  /** Whether a sheep stands on the tile the mower is about to drive into. */
  private sheepAhead(): boolean {
    const m = this.m;
    return this.footprintAt(m.x + dx(m.dir), m.y + dy(m.dir), m.dir).some((p) => {
      const i = this.critters.indexAt(p.x, p.y);
      return i >= 0 && this.critters.list[i]?.kind === 'sheep';
    });
  }

  private nudgeSheep(): boolean {
    if (this.cutWidth !== 1) return false;
    const m = this.m;
    const ax = m.x + dx(m.dir);
    const ay = m.y + dy(m.dir);
    if (this.grid.isBlockedFor(ax, ay, this.mowerSpec.cutPower)) return false;
    const r = this.critters.nudge(ax, ay, m.dir, this.currentTick, this.critterWorld);
    if (!r.moved) return false;
    const pen = this.critters.list[r.index]?.pen ?? -1;
    this.events.push({
      type: 'nudged',
      tick: this.currentTick,
      index: r.index,
      penned: r.penned,
      ...(r.penned && this.level.roundup ? { pen } : {}),
    });
    // (A round-up's end is checked once every mower has moved.)
    if (r.penned && !this.level.roundup) this.checkHerdWin();
    return this.canOccupy(ax, ay, m.dir);
  }

  /**
   * A mowing pass inside a meadow patch keeps it from growing back for a while; mowing its last
   * blade finishes it for good.
   */
  private tendPatch(x: number, y: number): void {
    const id = this.patchOf[y * this.grid.width + x] ?? -1;
    const patch = this.patches[id];
    if (!patch || patch.done) return;
    patch.lastMow = this.currentTick;
    const w = this.grid.width;
    patch.done = patch.tiles.every(
      (i) => this.grid.tileAt(i % w, Math.floor(i / w)) === Tile.Mowed,
    );
  }

  /** Half-mowed meadow patches left too long grow back. */
  private updateRegrowth(): void {
    const spec = this.level.regrow;
    if (!spec) return;
    const delay = spec.delayTiles * this.mowerSpec.ticksPerTile;
    const w = this.grid.width;
    for (const patch of this.patches) {
      if (patch.done || patch.lastMow < 0 || this.currentTick - patch.lastMow <= delay) continue;
      const tiles: { x: number; y: number }[] = [];
      for (const i of patch.tiles) {
        const x = i % w;
        const y = Math.floor(i / w);
        if (this.grid.regrow(x, y)) tiles.push({ x, y });
      }
      patch.lastMow = -1;
      if (tiles.length > 0) this.events.push({ type: 'regrew', tick: this.currentTick, tiles });
    }
  }

  /** Moles: their state (null on lawns without moles). */
  get moles(): Readonly<MoleState> | null {
    return this.moleState;
  }

  /**
   * Moles: every so often (paced to the mower) the lawn trembles on a mowed tile well away from the
   * mower, and a moment later a molehill comes up there.
   */
  private updateMoles(): void {
    const s = this.moleState;
    const spec = this.level.moles;
    if (!s || !spec || s.raised >= spec.hills) return;
    if (s.warn) {
      if (--s.warn.left > 0) return;
      const { x, y } = s.warn;
      s.warn = null;
      // Not under the mower: try somewhere else shortly.
      if (this.footprintAt(this.m.x, this.m.y, this.m.dir).some((p) => p.x === x && p.y === y)) {
        s.timer = MOLE_RETRY_TICKS;
        return;
      }
      if (!this.grid.raiseMolehill(x, y)) {
        s.timer = MOLE_RETRY_TICKS;
        return;
      }
      s.raised++;
      s.timer = spec.every * this.mowerSpec.ticksPerTile;
      this.events.push({ type: 'molehill', tick: this.currentTick, x, y });
      return;
    }
    if (--s.timer > 0) return;
    const spot = this.moleSpot();
    if (!spot) {
      s.timer = MOLE_RETRY_TICKS;
      return;
    }
    s.warn = { ...spot, left: MOLE_WARN_TICKS };
    this.events.push({ type: 'moleWarning', tick: this.currentTick, ...spot });
  }

  /**
   * Where the next molehill comes up: mowed lawn at least 3 tiles from the mower and from where it
   * will next steer, not next to another molehill, nor under a critter or a pickup.
   */
  private moleSpot(): { x: number; y: number } | null {
    const { width, height } = this.grid;
    const m = this.m;
    const next = this.nextDecisionTile();
    const far = (x: number, y: number) =>
      Math.abs(x - m.x) + Math.abs(y - m.y) >= 3 &&
      Math.abs(x - next.x) + Math.abs(y - next.y) >= 3;
    const pickupAt = (x: number, y: number) =>
      (this.level.pickups ?? []).some((p, i) => p.x === x && p.y === y && !this.isCollected(i));
    const spots: { x: number; y: number }[] = [];
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (this.grid.tileAt(x, y) !== Tile.Mowed || !far(x, y)) continue;
        if (this.critters.occupied(x, y) || pickupAt(x, y)) continue;
        const nearHill = [
          [0, -1],
          [1, 0],
          [0, 1],
          [-1, 0],
        ].some(([ox, oy]) => this.grid.tileAt(x + ox!, y + oy!) === Tile.Molehill);
        if (!nearHill) spots.push({ x, y });
      }
    }
    return spots.length > 0 ? this.moleRng.pick(spots) : null;
  }

  /** Stray balls: the ball in play, or null. */
  get ball(): Readonly<BallState> | null {
    return this.ballState;
  }

  /** Stray balls: ticks a ball takes to roll one tile (twice the mower's pace). */
  ballTicks(): number {
    return Math.max(2, Math.ceil(this.mowerSpec.ticksPerTile / BALL_SPEEDUP));
  }

  /** Whether a ball can roll onto (x, y): on the lawn, not into anything solid or a critter. */
  private ballCanRoll(x: number, y: number): boolean {
    return (
      this.grid.inBounds(x, y) &&
      !isSolid(this.grid.tileAt(x, y)) &&
      this.grid.tileAt(x, y) !== Tile.Brush &&
      !this.critters.occupied(x, y) &&
      !this.rivalOccupies(x, y)
    );
  }

  /**
   * The tiles a ball would roll over from (x, y) heading `dir`, until it meets something (the
   * start tile first).
   */
  ballRun(x: number, y: number, dir: Direction): { x: number; y: number }[] {
    const run: { x: number; y: number }[] = [];
    for (let cx = x, cy = y; this.ballCanRoll(cx, cy); cx += dx(dir), cy += dy(dir)) {
      run.push({ x: cx, y: cy });
    }
    return run;
  }

  /**
   * Stray balls: a whistle, then a ball rolls down a lane (a row or a column) from the edge. Never
   * a lane right beside the mower or where it will next steer: the danger is driving into the
   * lane while the ball is coming.
   */
  private updateBall(): void {
    const spec = this.level.balls;
    if (!spec) return;
    const b = this.ballState;
    if (!b) {
      if (--this.ballTimer > 0) return;
      const lane = this.pickBallLane();
      if (!lane) {
        this.ballTimer = BALL_RETRY_TICKS;
        return;
      }
      this.ballState = { ...lane, progress: 0, warn: BALL_WARN_TICKS };
      this.events.push({ type: 'ballWarning', tick: this.currentTick, ...lane });
      return;
    }
    if (b.warn > 0) {
      b.warn--;
      return;
    }
    if (this.ballHitsMower()) return;
    b.progress++;
    if (b.progress < this.ballTicks()) return;
    b.progress = 0;
    const nx = b.x + dx(b.dir);
    const ny = b.y + dy(b.dir);
    if (!this.ballCanRoll(nx, ny)) {
      this.endBall(b.x, b.y);
      return;
    }
    b.x = nx;
    b.y = ny;
    this.ballHitsMower();
  }

  /** The ball meets the mower: a heart (unless shielded or just bumped), and it bounces away. */
  private ballHitsMower(): boolean {
    const b = this.ballState;
    if (!b || b.warn > 0) return false;
    const m = this.m;
    const ballTile =
      b.progress * 2 < this.ballTicks() ? b : { x: b.x + dx(b.dir), y: b.y + dy(b.dir) };
    const tiles = this.footprintAt(m.x, m.y, m.dir);
    if (m.progress * 2 >= this.crossTicks()) {
      tiles.push(...this.footprintAt(m.x + dx(m.dir), m.y + dy(m.dir), m.dir));
    }
    if (!tiles.some((p) => p.x === ballTile.x && p.y === ballTile.y)) return false;
    if (m.invulnTicks === 0 && this.boostLeft.shield === 0) {
      m.hp--;
      m.invulnTicks = CRASH_INVULN_TICKS;
      this.events.push({
        type: 'ballHit',
        tick: this.currentTick,
        x: ballTile.x,
        y: ballTile.y,
        hpLeft: m.hp,
      });
      if (m.hp <= 0) {
        this.currentStatus = 'broken';
        this.events.push({ type: 'mowerBroken', tick: this.currentTick, x: m.x, y: m.y });
      }
    }
    this.endBall(ballTile.x, ballTile.y);
    return true;
  }

  private endBall(x: number, y: number): void {
    this.ballState = null;
    this.ballTimer = (this.level.balls?.every ?? 1) * this.mowerSpec.ticksPerTile;
    this.events.push({ type: 'ballGone', tick: this.currentTick, x, y });
  }

  /** A lane for the next ball: a row or column with a long enough run, clear of the mower. */
  private pickBallLane(): { x: number; y: number; dir: Direction } | null {
    const { width: w, height: h } = this.grid;
    const m = this.m;
    const next = this.nextDecisionTile();
    const options: { x: number; y: number; dir: Direction }[] = [];
    const nearRow = (y: number) => Math.abs(y - m.y) <= 1 || Math.abs(y - next.y) <= 1;
    const nearCol = (x: number) => Math.abs(x - m.x) <= 1 || Math.abs(x - next.x) <= 1;
    for (let y = 0; y < h; y++) {
      if (nearRow(y)) continue;
      options.push({ x: 0, y, dir: Dir.E }, { x: w - 1, y, dir: Dir.W });
    }
    for (let x = 0; x < w; x++) {
      if (nearCol(x)) continue;
      options.push({ x, y: 0, dir: Dir.S }, { x, y: h - 1, dir: Dir.N });
    }
    const good = options.filter((o) => this.ballRun(o.x, o.y, o.dir).length >= BALL_MIN_RUN);
    return good.length > 0 ? this.ballRng.pick(good) : null;
  }

  /**
   * Stray balls, for the autopilot: whether the ball (coming or rolling) will be on (x, y) at any
   * time between `from` and `to` ticks from now.
   */
  ballDanger(x: number, y: number, from: number, to: number): boolean {
    const b = this.ballState;
    if (!b) return false;
    const run = this.ballRun(b.x, b.y, b.dir);
    const k = run.findIndex((p) => p.x === x && p.y === y);
    if (k < 0) return false;
    const bt = this.ballTicks();
    const enter = b.warn + k * bt - b.progress - bt;
    const leave = enter + 2 * bt;
    return enter <= to && leave >= from;
  }

  /** Falling trees: each tree's state, in the level's order. */
  get falls(): readonly Readonly<FallState>[] {
    return this.fallStates;
  }

  /**
   * Falling trees: when its time comes a dead tree creaks for a moment, then falls, unless the
   * mower (or where it will next steer), a critter or the robot is near where it would land: then
   * it holds on, creaking, until the way is clear. Never on top of anyone.
   */
  private updateFalls(): void {
    const falls = this.level.falls;
    if (!falls) return;
    falls.forEach((f, index) => {
      const s = this.fallStates[index]!;
      if (s.down) return;
      if (s.timer > 0) {
        if (--s.timer === 0) {
          s.warn = FALL_WARN_TICKS;
          this.events.push({ type: 'treeCreak', tick: this.currentTick, index });
        }
        return;
      }
      if (s.warn > 1) {
        s.warn--;
        return;
      }
      const tiles = fallTiles(f);
      if (!this.clearToFall(tiles)) return;
      s.down = true;
      s.warn = 0;
      this.grid.topple(f.x, f.y);
      for (const p of tiles) this.grid.bury(p.x, p.y);
      this.events.push({
        type: 'treeFell',
        tick: this.currentTick,
        index,
        x: f.x,
        y: f.y,
        dir: f.dir,
        tiles,
      });
      // The log may have covered the last of the grass.
      if (
        this.grid.grassRemaining === 0 &&
        !this.level.art?.goal &&
        !this.level.soccer &&
        !this.level.herd &&
        !this.level.rival &&
        this.currentStatus === 'running'
      ) {
        this.currentStatus = 'won';
        this.events.push({ type: 'levelComplete', tick: this.currentTick });
      }
    });
  }

  /** Nobody within a tile of where a log would land (the mower: its tiles and its next turn). */
  private clearToFall(tiles: readonly { x: number; y: number }[]): boolean {
    const m = this.m;
    const near = [...this.footprintAt(m.x, m.y, m.dir)];
    const next = this.nextDecisionTile();
    near.push(...this.footprintAt(next.x, next.y, m.dir));
    for (const t of tiles) {
      if (near.some((p) => Math.abs(p.x - t.x) + Math.abs(p.y - t.y) <= 1)) return false;
      if (this.critters.occupied(t.x, t.y) || this.rivalOccupies(t.x, t.y)) return false;
    }
    return true;
  }

  /** Boss rival: the robot mower (null on other lawns). */
  get rival(): Readonly<RivalState> | null {
    return this.rivalState;
  }

  /** Boss rival: ticks the robot takes per tile, paced to the player's mower. */
  rivalCrossTicks(): number {
    return Math.ceil((this.mowerSpec.ticksPerTile * 100) / (this.level.rival?.speed ?? 100));
  }

  /**
   * Boss rival: whether the robot is on (or driving onto) a tile. The tile it is leaving is free
   * once it is more than halfway out, so it never holds a way out for long.
   */
  rivalOccupies(x: number, y: number): boolean {
    const r = this.rivalState;
    if (!r) return false;
    if (r.progress === 0) return r.x === x && r.y === y;
    if (r.x + dx(r.dir) === x && r.y + dy(r.dir) === y) return true;
    return r.x === x && r.y === y && r.progress * 2 < this.rivalCrossTicks();
  }

  /** Boss rival: grass tiles mowed by the player and by the robot. */
  rivalScore(): { you: number; robot: number } | null {
    const r = this.rivalState;
    if (!r) return null;
    const mowed = this.grid.totalGrass - this.grid.grassRemaining;
    return { you: mowed - r.tiles, robot: r.tiles };
  }

  /** Boss rival: where the robot may drive: open lawn, never onto or beside the player or a critter. */
  private rivalPassable(x: number, y: number): boolean {
    if (!this.grid.inBounds(x, y) || this.grid.isBlockedFor(x, y, 1)) return false;
    const t = this.grid.tileAt(x, y);
    if (
      t === Tile.FlowerBed ||
      t === Tile.Ornament ||
      t === Tile.Hose ||
      t === Tile.HoseChewed ||
      t === Tile.Sprinkler ||
      t === Tile.Thorns
    )
      return false;
    if (this.critters.occupied(x, y)) return false;
    // It keeps out of the mower's way: off the tiles it is on, and a tile clear all round where it
    // will next steer, so it can never box the player in.
    const m = this.m;
    if (this.footprintAt(m.x, m.y, m.dir).some((p) => p.x === x && p.y === y)) return false;
    const next = this.nextDecisionTile();
    return !this.footprintAt(next.x, next.y, m.dir).some(
      (p) => Math.abs(p.x - x) + Math.abs(p.y - y) <= 1,
    );
  }

  /**
   * Boss rival: the robot heads for the nearest grass still to mow (waiting when the way is taken)
   * and mows each tile it reaches.
   */
  private updateRival(): void {
    const r = this.rivalState;
    if (!r) return;
    if (r.progress === 0) {
      const dir = rivalHeading(this.grid, r, (x, y) => this.rivalPassable(x, y));
      if (dir === null) return;
      r.dir = dir;
    }
    r.progress++;
    if (r.progress < this.rivalCrossTicks()) return;
    r.x += dx(r.dir);
    r.y += dy(r.dir);
    r.progress = 0;
    if (this.grid.mow(r.x, r.y, r.dir, 1, 1) !== 'cut') return;
    r.tiles++;
    this.events.push({ type: 'rivalMowed', tick: this.currentTick, x: r.x, y: r.y, dir: r.dir });
    this.checkRivalEnd();
  }

  /**
   * Boss rival: lost as soon as the robot has mowed half the lawn (it can no longer be beaten);
   * won once the lawn is all mowed with more of it the player's.
   */
  private checkRivalEnd(): void {
    const score = this.rivalScore();
    if (!score || this.currentStatus !== 'running') return;
    if (score.robot * 2 >= this.grid.totalGrass) {
      this.currentStatus = 'outmowed';
      this.events.push({ type: 'outmowed', tick: this.currentTick });
    } else if (this.grid.grassRemaining === 0) {
      this.currentStatus = 'won';
      this.events.push({ type: 'levelComplete', tick: this.currentTick });
    }
  }

  /** Boss herding: every sheep penned. */
  private allPenned(): boolean {
    return this.critters.list.every((c) => c.kind !== 'sheep' || c.penned);
  }

  /** Boss herding: won once every sheep is penned and the whole lawn is mowed. */
  private checkHerdWin(): void {
    if (this.currentStatus !== 'running' || this.grid.grassRemaining > 0 || !this.allPenned())
      return;
    this.currentStatus = 'won';
    this.events.push({ type: 'levelComplete', tick: this.currentTick });
  }

  /** Boss herding: sheep in the pen, and how many there are. */
  sheepCount(): { penned: number; total: number } | null {
    if (!this.level.herd) return null;
    const sheep = this.critters.list.filter((c) => c.kind === 'sheep');
    return { penned: sheep.filter((c) => c.penned).length, total: sheep.length };
  }

  private crash(): void {
    const m = this.m;
    const ahead = this.footprintAt(m.x + dx(m.dir), m.y + dy(m.dir), m.dir);
    const critter = ahead
      .map((p) => this.critters.indexAt(p.x, p.y))
      .reduce((found, i) => (found >= 0 ? found : i), -1);
    const kind = critter >= 0 ? this.critters.list[critter]?.kind : undefined;
    // A lawn item this mower is too weak to break (a stronger one would have smashed it).
    const tooTough =
      critter < 0 &&
      ahead.some((p) => this.grid.toughnessAt(p.x, p.y) > (this.mowerSpec.breakPower ?? 0));
    // The shield boost takes the knock: no heart lost (and nothing to pay for the dog).
    if (m.invulnTicks === 0 && this.boostLeft.shield === 0) {
      // The Bumper takes the first crashes of a lawn: a crash all the same, but no hit lost.
      const bumper = m.bumper > 0;
      if (bumper) m.bumper--;
      else m.hp--;
      if (kind === 'dog') this.dogBumpCount++;
      this.events.push({
        type: 'crashed',
        tick: this.currentTick,
        x: m.x,
        y: m.y,
        dir: m.dir,
        hpLeft: m.hp,
        ...(kind ? { critter: kind } : {}),
        ...(tooTough ? { tooTough: true as const } : {}),
        ...(bumper ? { bumper: true as const } : {}),
      });
    }
    if (
      critter >= 0 &&
      this.critters.shoo(critter, m.x, m.y, this.currentTick, this.critterWorld)
    ) {
      this.events.push({ type: 'critterShooed', tick: this.currentTick, index: critter });
    }
    if (m.hp <= 0) {
      this.currentStatus = 'broken';
      this.events.push({ type: 'mowerBroken', tick: this.currentTick, x: m.x, y: m.y });
      return;
    }
    // Survivable hit: bounce back the way we came.
    m.dir = opposite(m.dir);
    this.flipSide();
    recordBounce(m);
    m.invulnTicks = CRASH_INVULN_TICKS;
    this.clearIntent();
  }

  /**
   * Everything that happens on reaching a tile center: pickups, then mowing, under the footprint.
   * Returns whether the pass cut tall grass (heavy going: it burns extra fuel).
   */
  private arriveAt(x: number, y: number): boolean {
    let heavy = false;
    for (const p of this.footprintAt(x, y, this.m.dir)) {
      if (!this.grid.inPlay(p.x, p.y)) continue;
      if (this.grid.tileAt(p.x, p.y) === Tile.Thorns) this.puncture(p.x, p.y);
      this.collectPickups(p.x, p.y);
      if (this.currentStatus === 'running' && this.mowAt(p.x, p.y)) heavy = true;
    }
    // Floating side reels mow the grass either side and ride up over everything else.
    if (this.mowerSpec.wings) {
      for (const p of wingTiles(x, y, this.m.dir)) this.wingPass(p.x, p.y);
    }
    return heavy;
  }

  /** Whether a side reel reaches (x, y): rules 9, over a secret yard beyond the lawn too. */
  private wingReaches(x: number, y: number): boolean {
    return this.rules.wingsBeyondLawn ? this.grid.inPlay(x, y) : this.grid.inBounds(x, y);
  }

  /**
   * A side reel passing over (x, y): it picks up what lies there and mows the grass (riding up
   * over everything else, and over a critter rather than through it).
   */
  private wingPass(x: number, y: number): void {
    if (this.currentStatus !== 'running' || !this.wingReaches(x, y)) return;
    this.collectPickups(x, y);
    if (!needsMowing(this.grid.tileAt(x, y)) || this.critters.occupied(x, y)) return;
    this.mowAt(x, y);
  }

  /**
   * Side reels swinging round a quarter turn: each sweeps through the corner between where it was
   * and where it ends up, so a turn leaves no corner uncut (a reversal keeps the same tiles).
   */
  private sweepWings(x: number, y: number, from: Direction, to: Direction): void {
    if (to === opposite(from)) return;
    const [oldL, oldR] = [turnLeft(from), turnRight(from)];
    const [newL, newR] = [turnLeft(to), turnRight(to)];
    for (const [a, b] of [
      [oldL, newL],
      [oldR, newR],
    ] as const) {
      this.wingPass(x + dx(a) + dx(b), y + dy(a) + dy(b));
      this.wingPass(x + dx(b), y + dy(b));
    }
  }

  /** Picks up what lies at (x, y); `pulled`: the coin magnet drew it in from there. */
  private collectPickups(x: number, y: number, pulled = false): void {
    const pickups = this.level.pickups ?? [];
    for (let i = 0; i < pickups.length; i++) {
      const p = pickups[i] as (typeof pickups)[number];
      if (p.x !== x || p.y !== y || this.collected[i]) continue;
      this.collected[i] = 1;
      const m = this.m;
      let spare = false;
      switch (p.kind) {
        case 'coin':
          this.coinCount++;
          this.drivers[this.seatIndex]!.coins++;
          break;
        case 'gas':
          // No tank (the reel): a coin instead.
          if (this.mowerSpec.fuelTiles === undefined) spare = true;
          else m.fuel = this.mowerSpec.fuelTiles;
          break;
        case 'repair':
          // A flat tire is patched first; then a lost heart. Nothing to repair: a coin instead.
          if (m.flat) {
            m.flat = false;
            this.events.push({ type: 'tirePatched', tick: this.currentTick, x, y });
          } else if (m.hp >= this.mowerSpec.maxHp) spare = true;
          else m.hp++;
          break;
        case 'rare':
          break;
        case 'treasure':
        case 'hoard':
          this.treasureCount += TREASURE_VALUE[p.kind] ?? 0;
          break;
        case 'turbo':
        case 'trim':
          this.held[p.kind]++;
          break;
      }
      if (spare) this.spareCount++;
      this.events.push({
        type: 'pickup',
        tick: this.currentTick,
        kind: p.kind,
        index: i,
        x,
        y,
        ...(spare ? { spare: true as const } : {}),
        ...(pulled ? { pulled: true as const } : {}),
      });
    }
  }

  /**
   * A thorn patch under a tire: a flat, unless one is flat already, Hardened Tires take it, or the
   * shield is up.
   */
  private puncture(x: number, y: number): void {
    const m = this.m;
    if (m.flat || this.boostLeft.shield > 0) return;
    const saved = m.tireGuard > 0;
    if (saved) m.tireGuard--;
    else m.flat = true;
    this.events.push({ type: 'flatTire', tick: this.currentTick, x, y, saved });
  }

  /** Mows one tile (with the mower's bite, or `bite`); returns whether it cut tall grass. */
  private mowAt(x: number, y: number, bite = this.mowerSpec.bite): boolean {
    if (!this.grid.inBounds(x, y)) {
      // Beyond the lawn only a secret yard's gate gives way, and it is the way in, not damage.
      const spec = this.mowerSpec;
      const opened =
        this.grid.smash(x, y) >= 0 ||
        this.grid.mow(x, y, this.m.dir, spec.cutPower, spec.bite) === 'cleared';
      if (opened) this.events.push({ type: 'gateOpened', tick: this.currentTick, x, y });
      return false;
    }
    const before = this.grid.tileAt(x, y);
    if (before === Tile.FlowerBed) {
      this.trampledCount++;
      this.events.push({ type: 'trampled', tick: this.currentTick, x, y });
      return false;
    }
    // A beehive: smashed to the ground, and the swarm stings (the shield keeps them off).
    if (before === Tile.Hive) {
      this.grid.smash(x, y);
      this.hiveCount++;
      const m = this.m;
      const stung = this.boostLeft.shield === 0 && m.invulnTicks === 0;
      if (stung) m.hp--;
      this.events.push({ type: 'hiveSmashed', tick: this.currentTick, x, y, stung, hpLeft: m.hp });
      if (m.hp <= 0) {
        this.currentStatus = 'broken';
        this.events.push({ type: 'mowerBroken', tick: this.currentTick, x: m.x, y: m.y });
      }
      return false;
    }
    // A lawn item the mower drove into (it is strong enough, or it could not have entered).
    const tier = this.grid.smash(x, y);
    if (tier >= 0) {
      this.smashedCount++;
      this.smashedByTier[tier] = (this.smashedByTier[tier] ?? 0) + 1;
      this.events.push({ type: 'smashed', tick: this.currentTick, x, y, tier });
      return false;
    }
    if (before === Tile.Sprinkler) {
      this.grid.mow(x, y, this.m.dir, this.mowerSpec.cutPower, this.mowerSpec.bite);
      this.sprinklerCount++;
      this.events.push({ type: 'sprinklerBroken', tick: this.currentTick, x, y });
      return false;
    }
    if (before === Tile.Hose) {
      this.grid.mow(x, y, this.m.dir, this.mowerSpec.cutPower, this.mowerSpec.bite);
      this.chewedCount++;
      this.events.push({ type: 'chewed', tick: this.currentTick, x, y });
      return false;
    }
    const spec = this.mowerSpec;
    const result: MowResult = this.grid.mow(x, y, this.m.dir, spec.cutPower, bite);
    if (result === 'none') return false;
    const fresh = result === 'cut';
    if (fresh) {
      this.drivers[this.seatIndex]!.tiles++;
      if (this.owners) this.owners[y * this.grid.width + x] = this.seatIndex;
    }
    this.tendPatch(x, y);
    this.events.push({
      type: 'tileMowed',
      tick: this.currentTick,
      x,
      y,
      dir: this.m.dir,
      fresh,
      result,
      ...(before === Tile.Molehill ? { flattened: true as const } : {}),
    });
    // Lawn art finishes at the judges' table instead (the picture can still be touched up), and
    // herding needs the sheep penned too.
    if (fresh && this.level.herd) {
      this.checkHerdWin();
    } else if (fresh && this.level.rival) {
      this.checkRivalEnd();
    } else if (
      fresh &&
      this.grid.grassRemaining === 0 &&
      !this.level.art?.goal &&
      !this.level.soccer &&
      // (A match's end is checked once every mower has moved.)
      this.drivers.length === 1
    ) {
      this.currentStatus = 'won';
      this.events.push({ type: 'levelComplete', tick: this.currentTick });
    }
    return isTallGrass(before);
  }
}
